import type { SourceRegion } from "./plan-regions";
import type { Fixture, Level, Opening, OutdoorArea, Room, Stair, Wall } from "./scene-data";
import { detectFurniture, type DetectedFixture, type DetectorWall, type FixtureObstacle } from "./furniture-detector.ts";
export type { DetectedFixture } from "./furniture-detector.ts";

export type Axis = "horizontal" | "vertical";

export type DetectedOpening = {
  kind: "door" | "window";
  offset: number;
  width: number;
  confidence: number;
  evidence?: "symbol" | "geometry" | "context";
};

export type DetectedWall = {
  id: string;
  axis: Axis;
  start: [number, number];
  end: [number, number];
  thickness: number;
  confidence: number;
  openings: DetectedOpening[];
  /** Heavy walls come from the strong-threshold mask; light walls are thinner
   * partitions recovered from a fainter mask and validated by requiring both
   * endpoints to terminate at another wall or the footprint edge. */
  weight: "heavy" | "light";
  /** Fractions [0,1] along the wall's run (from start to end) where the wall is
   *  a thin balustrade rather than structural. Set only on walls adjacent to a
   *  stairwell opening and only when the profile is genuinely mixed. */
  railSpans?: Array<[number, number]>;
};

export type DetectedOutdoorArea = {
  id: string;
  side: "top" | "right" | "bottom" | "left";
  x: number;
  y: number;
  width: number;
  height: number;
  confidence: number;
};

export type DetectedRoom = {
  id: string;
  /** Bounding rectangle of the enclosed region in deskewed pixel space. Rooms
   * are approximated by their bounding box, not an exact rectilinear outline:
   * correct for the rectangular rooms typical of residential plans, but an
   * L-shaped room will report a box that extends past its true footprint. */
  polygon: [number, number][];
  areaPx: number;
  confidence: number;
};

export type DetectedStair = {
  id: string;
  runAxis: Axis;
  x: number;
  y: number;
  width: number;
  height: number;
  stepCount: number;
  confidence: number;
  /** Which end of the run-axis goes UP. "start" = the (x,y) corner; "end" = the
   *  far corner (x+width, y+height). Absent when the arrow could not be found
   *  reliably; callers must fall back to the existing heuristic in that case. */
  ascend?: "start" | "end";
  /**
   * A turned stair covers an L, not the rectangle bounding it: winder treads
   * fan across the full width at one end, and the straight flight runs down one
   * side of the rest. Both are given when the two can be told apart, and x/y/
   * width/height stay the bounding box of the pair, so anything reasoning about
   * the shaft as a whole — the slab opening, the flight-to-flight connection —
   * is unaffected.
   */
  winder?: { x: number; y: number; width: number; height: number };
  flight?: { x: number; y: number; width: number; height: number };
};

export type DetectedStructure = {
  regionId: string;
  sourceWidth: number;
  sourceHeight: number;
  walls: DetectedWall[];
  outdoorAreas: DetectedOutdoorArea[];
  stairs: DetectedStair[];
  rooms: DetectedRoom[];
  fixtures: DetectedFixture[];
  floorTextureUrl?: string;
  footprint: { x: number; y: number; width: number; height: number };
  roomCount: number;
  confidence: number;
  diagnostics: {
    threshold: number;
    wallThickness: number;
    geometryVotes: number;
    topologyVotes: number;
    openingVotes: number;
    stairVotes: number;
    rotationDegrees?: number;
  };
  /** Rotation of the source plan relative to its own principal wall axes. */
  sourceRotationDegrees?: number;
  rotationCenter?: [number, number];
  /**
   * Everything the fixture pass needs, carried so it can be re-run once the
   * scale shared by every level is known. Removed before the structures are
   * returned; nothing outside this module should read it.
   */
  fixtureInputs?: FixtureInputs;
};

/** Inputs for a re-run of fixture detection at the shared project scale. */
type FixtureInputs = {
  mask: Uint8Array;
  width: number;
  footprint: Bounds;
  wallThickness: number;
  obstacles: FixtureObstacle[];
  walls: DetectorWall[];
};

type Bounds = { minX: number; minY: number; maxX: number; maxY: number };
type Segment = {
  axis: Axis;
  line: number;
  from: number;
  to: number;
  thickness: number;
  density: number;
  weight?: "heavy" | "light";
};

type GapEvidence = {
  kind: "door" | "window";
  confidence: number;
  evidence: "symbol" | "geometry" | "context";
} | null;

const clamp = (value: number, minimum: number, maximum: number) => Math.max(minimum, Math.min(maximum, value));

function luminance(pixels: ArrayLike<number>, index: number) {
  return 0.299 * pixels[index] + 0.587 * pixels[index + 1] + 0.114 * pixels[index + 2];
}

/**
 * Saturation of a pixel, 0 for any grey and rising toward 1 for a strong hue.
 *
 * Printed floorplans routinely draw structure in colour rather than black:
 * tan or brown exterior walls, blue bathroom fills, olive hatching. Judging
 * "is this ink?" on luminance alone treats a mid-tone brown wall as paper,
 * which is why the footprint of a colour-drawn plan can collapse to a
 * fraction of the real building.
 */
function saturation(pixels: ArrayLike<number>, index: number) {
  const maximum = Math.max(pixels[index], pixels[index + 1], pixels[index + 2]);
  const minimum = Math.min(pixels[index], pixels[index + 1], pixels[index + 2]);
  return maximum === 0 ? 0 : (maximum - minimum) / maximum;
}

/**
 * Ink test used for the building envelope: a pixel is ink when it is dark, or
 * when it is clearly coloured and not near-white. Deliberately more permissive
 * than the wall-tracing masks, because its job is to bound the drawing rather
 * than to decide what is structural.
 */
function isEnvelopeInk(pixels: ArrayLike<number>, index: number, threshold: number) {
  if (pixels[index + 3] <= 32) return false;
  const value = luminance(pixels, index);
  if (value < threshold) return true;
  return saturation(pixels, index) > 0.25 && value < 225;
}

/**
 * Extent of the drawing itself, measured straight from the ink.
 *
 * The footprint used to be inferred from whichever wall segments the tracer
 * managed to accept, so a plan whose walls were too thin or too colourful to
 * trace produced a footprint far smaller than the building — in the worst
 * observed case a single pixel tall. Every later stage is expressed relative
 * to the footprint (exterior-wall tests, balcony depth, stair plausibility,
 * the room grid, metric scale), so that error silently corrupted all of them.
 * Measuring the envelope from ink keeps it independent of tracing success.
 *
 * Rows and columns holding only a trace of ink are dropped first, so a
 * dimension chain or a caption outside the building does not inflate it.
 */
function inkEnvelope(
  pixels: ArrayLike<number>,
  width: number,
  bounds: Bounds,
  threshold: number,
): Bounds | null {
  const spanX = bounds.maxX - bounds.minX + 1;
  const spanY = bounds.maxY - bounds.minY + 1;
  if (spanX <= 2 || spanY <= 2) return null;
  const columns = new Uint32Array(spanX);
  const rows = new Uint32Array(spanY);
  let total = 0;
  for (let y = bounds.minY; y <= bounds.maxY; y += 1) {
    for (let x = bounds.minX; x <= bounds.maxX; x += 1) {
      if (!isEnvelopeInk(pixels, (y * width + x) * 4, threshold)) continue;
      columns[x - bounds.minX] += 1;
      rows[y - bounds.minY] += 1;
      total += 1;
    }
  }
  if (total < 64) return null;

  // A structural line crossing the plan marks a large share of its row or
  // column; stray text and dimension ticks mark very few. Requiring a small
  // fraction of the span separates the two without needing a tuned pixel count.
  const columnFloor = Math.max(2, spanY * 0.02);
  const rowFloor = Math.max(2, spanX * 0.02);
  let minX = -1;
  let maxX = -1;
  for (let index = 0; index < spanX; index += 1) {
    if (columns[index] < columnFloor) continue;
    if (minX < 0) minX = index;
    maxX = index;
  }
  let minY = -1;
  let maxY = -1;
  for (let index = 0; index < spanY; index += 1) {
    if (rows[index] < rowFloor) continue;
    if (minY < 0) minY = index;
    maxY = index;
  }
  if (minX < 0 || minY < 0 || maxX - minX < 4 || maxY - minY < 4) return null;
  return {
    minX: bounds.minX + minX,
    minY: bounds.minY + minY,
    maxX: bounds.minX + maxX,
    maxY: bounds.minY + maxY,
  };
}

function otsuThreshold(pixels: ArrayLike<number>, width: number, bounds: Bounds) {
  const histogram = new Uint32Array(256);
  let total = 0;
  for (let y = bounds.minY; y <= bounds.maxY; y += 1) {
    for (let x = bounds.minX; x <= bounds.maxX; x += 1) {
      const index = (y * width + x) * 4;
      if (pixels[index + 3] <= 32) continue;
      histogram[Math.round(luminance(pixels, index))] += 1;
      total += 1;
    }
  }

  let sum = 0;
  for (let value = 0; value < 256; value += 1) sum += value * histogram[value];
  let backgroundWeight = 0;
  let backgroundSum = 0;
  let bestVariance = -1;
  let threshold = 145;
  for (let value = 0; value < 256; value += 1) {
    backgroundWeight += histogram[value];
    if (!backgroundWeight) continue;
    const foregroundWeight = total - backgroundWeight;
    if (!foregroundWeight) break;
    backgroundSum += value * histogram[value];
    const backgroundMean = backgroundSum / backgroundWeight;
    const foregroundMean = (sum - backgroundSum) / foregroundWeight;
    const variance = backgroundWeight * foregroundWeight * (backgroundMean - foregroundMean) ** 2;
    if (variance > bestVariance) {
      bestVariance = variance;
      threshold = value;
    }
  }
  return clamp(threshold + 18, 105, 188);
}

function paperBounds(pixels: ArrayLike<number>, width: number, height: number, region: SourceRegion): Bounds {
  const raw: Bounds = {
    minX: clamp(Math.floor(region.x * width), 0, width - 1),
    minY: clamp(Math.floor(region.y * height), 0, height - 1),
    maxX: clamp(Math.ceil((region.x + region.width) * width) - 1, 0, width - 1),
    maxY: clamp(Math.ceil((region.y + region.height) * height) - 1, 0, height - 1),
  };

  const acceptableRows: boolean[] = [];
  for (let y = raw.minY; y <= raw.maxY; y += 1) {
    let paper = 0;
    let sampled = 0;
    for (let x = raw.minX; x <= raw.maxX; x += 3) {
      const index = (y * width + x) * 4;
      if (pixels[index + 3] > 32 && luminance(pixels, index) > 205) paper += 1;
      sampled += 1;
    }
    // Phone chrome is almost completely dark; a floorplan wall can legitimately
    // occupy most of a row, so retain any row that still has a paper margin.
    acceptableRows.push(sampled > 0 && paper / sampled >= 0.08);
  }

  let bestStart = 0;
  let bestEnd = acceptableRows.length - 1;
  let runStart = -1;
  acceptableRows.forEach((acceptable, index) => {
    if (acceptable && runStart < 0) runStart = index;
    if ((!acceptable || index === acceptableRows.length - 1) && runStart >= 0) {
      const runEnd = acceptable && index === acceptableRows.length - 1 ? index : index - 1;
      if (runEnd - runStart > bestEnd - bestStart || bestEnd === acceptableRows.length - 1) {
        bestStart = runStart;
        bestEnd = runEnd;
      }
      runStart = -1;
    }
  });

  if (bestEnd - bestStart >= (raw.maxY - raw.minY) * 0.38) {
    raw.minY += bestStart;
    raw.maxY = raw.minY + (bestEnd - bestStart);
  }
  return raw;
}

function createMask(
  pixels: ArrayLike<number>,
  width: number,
  height: number,
  bounds: Bounds,
  threshold: number,
) {
  const mask = new Uint8Array(width * height);
  for (let y = bounds.minY; y <= bounds.maxY; y += 1) {
    let rowDark = 0;
    for (let x = bounds.minX; x <= bounds.maxX; x += 1) {
      const index = (y * width + x) * 4;
      if (pixels[index + 3] > 32 && luminance(pixels, index) < threshold) {
        mask[y * width + x] = 1;
        rowDark += 1;
      }
    }
    if (rowDark / Math.max(1, bounds.maxX - bounds.minX + 1) > 0.94) {
      for (let x = bounds.minX; x <= bounds.maxX; x += 1) mask[y * width + x] = 0;
    }
  }
  return mask;
}

/**
 * Floorplans need not be aligned to the image axes. Score pairs of dark pixels
 * along two perpendicular directions and recover the plan's dominant local
 * frame. Text and furniture contain short strokes in many directions; walls
 * produce repeated support over several longer distances.
 */
function estimateDominantPlanRotation(
  mask: Uint8Array,
  width: number,
  height: number,
  bounds: Bounds,
) {
  const minimumDimension = Math.max(1, Math.min(bounds.maxX - bounds.minX, bounds.maxY - bounds.minY));
  const distances = [
    Math.max(7, Math.round(minimumDimension * 0.035)),
    Math.max(12, Math.round(minimumDimension * 0.075)),
    Math.max(18, Math.round(minimumDimension * 0.13)),
  ];
  const points: Array<[number, number]> = [];
  const stride = minimumDimension > 500 ? 3 : 2;
  for (let y = bounds.minY + 2; y <= bounds.maxY - 2; y += stride) {
    for (let x = bounds.minX + 2; x <= bounds.maxX - 2; x += stride) {
      if (!mask[y * width + x]) continue;
      let neighbourhood = 0;
      for (let oy = -1; oy <= 1; oy += 1) {
        for (let ox = -1; ox <= 1; ox += 1) neighbourhood += mask[(y + oy) * width + x + ox];
      }
      // Suppress isolated text/anti-alias pixels while retaining thin symbols.
      if (neighbourhood >= 3) points.push([x, y]);
    }
  }
  if (points.length < 40) return 0;

  const supported = (x: number, y: number) => {
    const px = Math.round(x);
    const py = Math.round(y);
    if (px < 1 || px >= width - 1 || py < 1 || py >= height - 1) return 0;
    let hit = 0;
    for (let oy = -1; oy <= 1; oy += 1) {
      for (let ox = -1; ox <= 1; ox += 1) hit = Math.max(hit, mask[(py + oy) * width + px + ox]);
    }
    return hit;
  };

  let bestAngle = 0;
  let bestScore = Number.NEGATIVE_INFINITY;
  let axisAlignedScore = Number.NEGATIVE_INFINITY;
  for (let degrees = -44; degrees <= 45; degrees += 1) {
    const angle = degrees * Math.PI / 180;
    const directions = [
      [Math.cos(angle), Math.sin(angle)],
      [-Math.sin(angle), Math.cos(angle)],
    ];
    let score = 0;
    const pointStride = Math.max(1, Math.ceil(points.length / 8500));
    for (let index = 0; index < points.length; index += pointStride) {
      const [x, y] = points[index];
      directions.forEach(([dx, dy]) => {
        distances.forEach((distance, distanceIndex) => {
          const forward = supported(x + dx * distance, y + dy * distance);
          const backward = supported(x - dx * distance, y - dy * distance);
          score += (forward + backward) * (distanceIndex + 1);
        });
      });
    }
    if (score > bestScore) {
      bestScore = score;
      bestAngle = degrees;
    }
    if (degrees === 0) axisAlignedScore = score;
  }
  // A one-degree preference commonly comes from asymmetric wall thickness or
  // rasterisation. Avoid resampling an already aligned drawing for that noise.
  return Math.abs(bestAngle) <= 1 || bestScore <= axisAlignedScore * 1.045 ? 0 : bestAngle;
}

function rotatePixelsAround(
  pixels: ArrayLike<number>,
  width: number,
  height: number,
  center: [number, number],
  sourceRotationDegrees: number,
) {
  const result = new Uint8ClampedArray(width * height * 4).fill(255);
  const angle = sourceRotationDegrees * Math.PI / 180;
  const cosine = Math.cos(angle);
  const sine = Math.sin(angle);
  const [centerX, centerY] = center;
  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      // Destination is the deskewed image. Sample the source by rotating the
      // destination point back into the photographed plan.
      const dx = x - centerX;
      const dy = y - centerY;
      const sourceX = centerX + dx * cosine - dy * sine;
      const sourceY = centerY + dx * sine + dy * cosine;
      if (sourceX < 0 || sourceX >= width - 1 || sourceY < 0 || sourceY >= height - 1) continue;
      // Use the darkest of the four sub-pixel neighbours. Ordinary bilinear
      // filtering can erase a one-pixel door arc or balcony rail; this
      // structure-preserving sampler retains those strokes for later masks.
      const candidates = [
        [Math.floor(sourceX), Math.floor(sourceY)],
        [Math.ceil(sourceX), Math.floor(sourceY)],
        [Math.floor(sourceX), Math.ceil(sourceY)],
        [Math.ceil(sourceX), Math.ceil(sourceY)],
      ];
      let sourceIndex = (candidates[0][1] * width + candidates[0][0]) * 4;
      let darkest = luminance(pixels, sourceIndex);
      candidates.slice(1).forEach(([candidateX, candidateY]) => {
        const candidateIndex = (candidateY * width + candidateX) * 4;
        const candidateLuminance = luminance(pixels, candidateIndex);
        if (candidateLuminance < darkest) {
          darkest = candidateLuminance;
          sourceIndex = candidateIndex;
        }
      });
      const destinationIndex = (y * width + x) * 4;
      result[destinationIndex] = pixels[sourceIndex];
      result[destinationIndex + 1] = pixels[sourceIndex + 1];
      result[destinationIndex + 2] = pixels[sourceIndex + 2];
      result[destinationIndex + 3] = pixels[sourceIndex + 3];
    }
  }
  return result;
}

function markLongRuns(mask: Uint8Array, width: number, height: number, bounds: Bounds, axis: Axis, minimumRun: number) {
  const result = new Uint8Array(width * height);
  const majorStart = axis === "horizontal" ? bounds.minY : bounds.minX;
  const majorEnd = axis === "horizontal" ? bounds.maxY : bounds.maxX;
  const minorStart = axis === "horizontal" ? bounds.minX : bounds.minY;
  const minorEnd = axis === "horizontal" ? bounds.maxX : bounds.maxY;

  for (let major = majorStart; major <= majorEnd; major += 1) {
    let start = -1;
    let lastDark = -1;
    let darkCount = 0;
    let gap = 0;
    const flush = () => {
      if (start < 0 || lastDark < start) return;
      const length = lastDark - start + 1;
      if (length >= minimumRun && darkCount / length >= 0.72) {
        for (let minor = start; minor <= lastDark; minor += 1) {
          const x = axis === "horizontal" ? minor : major;
          const y = axis === "horizontal" ? major : minor;
          result[y * width + x] = 1;
        }
      }
    };

    for (let minor = minorStart; minor <= minorEnd; minor += 1) {
      const x = axis === "horizontal" ? minor : major;
      const y = axis === "horizontal" ? major : minor;
      if (mask[y * width + x]) {
        if (start < 0) start = minor;
        lastDark = minor;
        darkCount += 1;
        gap = 0;
      } else if (start >= 0) {
        gap += 1;
        if (gap > 1) {
          flush();
          start = -1;
          lastDark = -1;
          darkCount = 0;
          gap = 0;
        }
      }
    }
    flush();
  }
  return result;
}

function maskComponents(mask: Uint8Array, width: number, height: number, bounds: Bounds, axis: Axis): Segment[] {
  const visited = new Uint8Array(mask.length);
  const components: Segment[] = [];
  const offsets = [[-1, 0], [1, 0], [0, -1], [0, 1]] as const;

  for (let y = bounds.minY; y <= bounds.maxY; y += 1) {
    for (let x = bounds.minX; x <= bounds.maxX; x += 1) {
      const startIndex = y * width + x;
      if (!mask[startIndex] || visited[startIndex]) continue;
      const queue: Array<[number, number]> = [[x, y]];
      visited[startIndex] = 1;
      let minX = x;
      let minY = y;
      let maxX = x;
      let maxY = y;
      let cells = 0;
      for (let cursor = 0; cursor < queue.length; cursor += 1) {
        const [cx, cy] = queue[cursor];
        cells += 1;
        minX = Math.min(minX, cx);
        minY = Math.min(minY, cy);
        maxX = Math.max(maxX, cx);
        maxY = Math.max(maxY, cy);
        offsets.forEach(([ox, oy]) => {
          const nx = cx + ox;
          const ny = cy + oy;
          if (nx < bounds.minX || nx > bounds.maxX || ny < bounds.minY || ny > bounds.maxY) return;
          const next = ny * width + nx;
          if (!mask[next] || visited[next]) return;
          visited[next] = 1;
          queue.push([nx, ny]);
        });
      }
      const boxWidth = maxX - minX + 1;
      const boxHeight = maxY - minY + 1;
      const length = axis === "horizontal" ? boxWidth : boxHeight;
      const thickness = axis === "horizontal" ? boxHeight : boxWidth;
      if (length / Math.max(1, thickness) < 2.2) continue;
      components.push({
        axis,
        line: axis === "horizontal" ? (minY + maxY) / 2 : (minX + maxX) / 2,
        from: axis === "horizontal" ? minX : minY,
        to: axis === "horizontal" ? maxX : maxY,
        thickness,
        density: cells / Math.max(1, boxWidth * boxHeight),
      });
    }
  }
  return components;
}

function splitByStrokeSupport(
  segments: Segment[],
  mask: Uint8Array,
  width: number,
  height: number,
  minimumRun: number,
  trimSingleSupportedRun = false,
) {
  return segments.flatMap((segment) => {
    if (segment.thickness < 3 || segment.to - segment.from < minimumRun * 1.6) return [segment];
    // Measure the contiguous ink core across the stroke and compare it to the
    // segment's own thickness. The question this test exists to answer is
    // whether a thin annotation has been fused onto a thick wall, and a ratio
    // against the wall's thickness answers it directly: a dimension line is a
    // small fraction of the wall it touches, while a wall that merely tapers
    // stays close to full width.
    //
    // Counting ink inside a fixed window sized from the segment's maximum
    // thickness asked a different question, and a wall drawn one pixel
    // narrower along part of its length failed it — three ink pixels in a
    // five-wide window scores 0.60 against a 0.62 cutoff, which silently
    // discarded the lower half of a real structural wall.
    const radius = Math.max(1, Math.floor(segment.thickness / 2)) + 1;
    const minimumCore = Math.max(2, segment.thickness * 0.5);
    const supported: boolean[] = [];
    for (let coordinate = Math.floor(segment.from); coordinate <= Math.ceil(segment.to); coordinate += 1) {
      let longest = 0;
      let run = 0;
      for (let offset = -radius; offset <= radius; offset += 1) {
        const x = Math.round(segment.axis === "horizontal" ? coordinate : segment.line + offset);
        const y = Math.round(segment.axis === "horizontal" ? segment.line + offset : coordinate);
        if (x < 0 || x >= width || y < 0 || y >= height) {
          run = 0;
          continue;
        }
        if (mask[y * width + x]) {
          run += 1;
          if (run > longest) longest = run;
        } else run = 0;
      }
      supported.push(longest >= minimumCore);
    }

    const pieces: Segment[] = [];
    let start = -1;
    let lastSupported = -1;
    let gap = 0;
    const flush = () => {
      if (start < 0 || lastSupported - start + 1 < Math.max(6, minimumRun * 0.24)) return;
      pieces.push({
        ...segment,
        from: segment.from + start,
        to: segment.from + lastSupported,
      });
    };
    supported.forEach((hasSupport, index) => {
      if (hasSupport) {
        if (start < 0) start = index;
        lastSupported = index;
        gap = 0;
      } else if (start >= 0) {
        gap += 1;
        if (gap > 1) {
          flush();
          start = -1;
          lastSupported = -1;
          gap = 0;
        }
      }
    });
    flush();
    if (pieces.length) {
      const supportedLength = pieces.reduce((sum, piece) => sum + piece.to - piece.from + 1, 0);
      const originalLength = segment.to - segment.from + 1;
      const trimmedLength = originalLength - supportedLength;
      const lonePiece = pieces[0];
      const attachedToOneEnd = pieces.length === 1 && (
        (lonePiece.from <= segment.from + 2 && lonePiece.to < segment.to - 2)
        || (lonePiece.to >= segment.to - 2 && lonePiece.from > segment.from + 2)
      );
      // A thick wall that meets a collinear dimension line is one connected
      // component: the component keeps the wall's thickness but inherits the
      // annotation's full length. Keep the one genuinely thick run when the
      // unsupported tail is substantial instead of restoring that virtual wall.
      if (
        pieces.length >= 2
        || (
          trimSingleSupportedRun
          && attachedToOneEnd
          && originalLength >= minimumRun * 5
          && trimmedLength >= Math.max(5, minimumRun * 0.45)
        )
      ) return pieces;
    }
    return [segment];
  });
}

function mergeOverlaps(segments: Segment[]) {
  const sorted = [...segments].sort((a, b) => (a.axis.localeCompare(b.axis)) || (a.line - b.line) || (a.from - b.from));
  const merged: Segment[] = [];
  for (const segment of sorted) {
    const previous = merged.at(-1);
    const lineTolerance = Math.max(2, Math.min(segment.thickness, previous?.thickness ?? segment.thickness) * 0.75);
    if (
      previous
      && previous.axis === segment.axis
      && Math.abs(previous.line - segment.line) <= lineTolerance
      && segment.from <= previous.to + 2
    ) {
      const previousLength = previous.to - previous.from + 1;
      const segmentLength = segment.to - segment.from + 1;
      const totalLength = previousLength + segmentLength;
      previous.line = (previous.line * previousLength + segment.line * segmentLength) / totalLength;
      previous.from = Math.min(previous.from, segment.from);
      previous.to = Math.max(previous.to, segment.to);
      previous.thickness = Math.max(previous.thickness, segment.thickness);
      previous.density = (previous.density * previousLength + segment.density * segmentLength) / totalLength;
    } else merged.push({ ...segment });
  }
  return merged;
}

function weightedPercentile(segments: Segment[], percentile: number) {
  const values = segments
    .map((segment) => ({ value: segment.thickness, weight: Math.max(1, segment.to - segment.from) }))
    .sort((a, b) => a.value - b.value);
  const total = values.reduce((sum, item) => sum + item.weight, 0);
  let accumulated = 0;
  for (const item of values) {
    accumulated += item.weight;
    if (accumulated >= total * percentile) return item.value;
  }
  return values.at(-1)?.value ?? 3;
}

function segmentsIntersect(a: Segment, b: Segment, tolerance: number) {
  if (a.axis === b.axis) return false;
  const horizontal = a.axis === "horizontal" ? a : b;
  const vertical = a.axis === "vertical" ? a : b;
  return vertical.line >= horizontal.from - tolerance
    && vertical.line <= horizontal.to + tolerance
    && horizontal.line >= vertical.from - tolerance
    && horizontal.line <= vertical.to + tolerance;
}

function structuralSegments(segments: Segment[], minimumDimension: number, wallThickness: number) {
  const minimumThickness = Math.max(2, wallThickness * 0.32);
  const initial = segments.filter((segment) => (
    segment.thickness >= minimumThickness
    // Short wall fragments immediately beside a doorway are structural when
    // they are thick and connect to a perpendicular partition. A 7.5% cutoff
    // dropped these fragments in tall phone screenshots whose paper bounds
    // include UI chrome; 6% retains them for the topology vote below.
    && segment.to - segment.from >= minimumDimension * 0.06
    && segment.density >= 0.32
  ));

  return initial.filter((segment) => {
    const length = segment.to - segment.from;
    const connected = initial.some((other) => other !== segment && segmentsIntersect(segment, other, Math.max(3, wallThickness * 0.8)));
    const collinearDoorwayContinuation = initial.some((other) => {
      if (other === segment || other.axis !== segment.axis) return false;
      if (Math.abs(other.line - segment.line) > Math.max(3, wallThickness * 0.7)) return false;
      const gap = Math.max(segment.from, other.from) - Math.min(segment.to, other.to);
      if (gap < 0 || gap > Math.max(16, minimumDimension * 0.14)) return false;
      const otherLength = other.to - other.from;
      const longEnoughPair = Math.max(length, otherLength) >= minimumDimension * 0.24;
      return longEnoughPair
        && length >= minimumDimension * 0.09
        && segment.thickness >= wallThickness * 0.45;
    });
    return length >= minimumDimension * 0.32
      || connected
      || collinearDoorwayContinuation
      || (segment.thickness >= wallThickness * 0.72 && length >= minimumDimension * 0.13);
  });
}

/**
 * A candidate endpoint is anchored when it terminates at a perpendicular wall
 * (a T or L junction) or at the footprint edge. This is the rule that lets a
 * genuinely thin partition survive while furniture and text, which float
 * free of the wall network, do not.
 */
function endpointAnchored(coordinate: number, line: number, axis: Axis, pool: Segment[], tolerance: number, footprint: Bounds) {
  const onEdge = axis === "horizontal"
    ? Math.abs(coordinate - footprint.minX) <= tolerance || Math.abs(coordinate - footprint.maxX) <= tolerance
    : Math.abs(coordinate - footprint.minY) <= tolerance || Math.abs(coordinate - footprint.maxY) <= tolerance;
  if (onEdge) return true;
  return pool.some((other) => (
    other.axis !== axis
    && Math.abs(other.line - coordinate) <= tolerance
    && line >= other.from - tolerance
    && line <= other.to + tolerance
  ));
}

/**
 * Recover thin partitions that a single strong-threshold mask would drop.
 * Candidates come from the fainter medium mask and must be both long enough
 * and, unlike the heavy tier, anchored at both ends against the already
 * accepted wall network. Two passes let a light wall anchor against another
 * light wall accepted in the first pass (e.g. an interior closet corner),
 * without ever letting an unanchored candidate validate itself.
 */
function lightStructuralSegments(
  candidates: Segment[],
  heavyPool: Segment[],
  minimumDimension: number,
  footprint: Bounds,
  tolerance: number,
) {
  const overlapsHeavy = (segment: Segment) => heavyPool.some((heavy) => (
    heavy.axis === segment.axis
    && Math.abs(heavy.line - segment.line) <= tolerance
    && Math.max(segment.from, heavy.from) <= Math.min(segment.to, heavy.to)
  ));
  let remaining = candidates.filter((segment) => (
    segment.to - segment.from >= Math.max(8, minimumDimension * 0.045)
    && segment.density >= 0.3
    && !overlapsHeavy(segment)
  ));

  const accepted: Segment[] = [];
  let pool = [...heavyPool];
  // Several passes let a chain of thin partitions bootstrap off a single
  // heavy wall or footprint edge, one T-junction at a time, instead of
  // requiring every partition to reach a heavy wall directly.
  for (let pass = 0; pass < 4; pass += 1) {
    const stillRemaining: Segment[] = [];
    remaining.forEach((segment) => {
      const anchored = endpointAnchored(segment.from, segment.line, segment.axis, pool, tolerance, footprint)
        && endpointAnchored(segment.to, segment.line, segment.axis, pool, tolerance, footprint);
      if (anchored) {
        const tagged: Segment = { ...segment, weight: "light" };
        accepted.push(tagged);
        pool = [...pool, tagged];
      } else {
        stillRemaining.push(segment);
      }
    });
    remaining = stillRemaining;
  }
  return accepted;
}

/**
 * Dimension chains are thin, near-perfectly straight, and carry short witness
 * ticks wherever a sub-measurement boundary crosses them, including in the
 * middle of the span, not just at their two ends the way a real wall's T or L
 * junctions do. A wall segment does not normally have another stroke crossing
 * its middle, so one interior tick is already strong annotation evidence.
 */
function hasInteriorTickMarks(
  mask: Uint8Array,
  width: number,
  height: number,
  axis: Axis,
  line: number,
  from: number,
  to: number,
  thickness: number,
) {
  const span = to - from;
  const margin = Math.max(6, span * 0.12);
  const innerFrom = from + margin;
  const innerTo = to - margin;
  if (innerTo <= innerFrom) return false;
  const step = Math.max(1, Math.round((innerTo - innerFrom) / 24));
  for (let coordinate = innerFrom; coordinate <= innerTo; coordinate += step) {
    const run = longestPerpendicularRun(mask, width, height, axis, coordinate, line, 18);
    if (run > thickness + 3 && run <= 16) return true;
  }
  return false;
}

function isDimensionAnnotation(
  mask: Uint8Array,
  width: number,
  height: number,
  segment: Segment,
) {
  if (segment.thickness > 3 || segment.density < 0.93) return false;
  return hasInteriorTickMarks(mask, width, height, segment.axis, segment.line, segment.from, segment.to, segment.thickness);
}

function recoverWallAnchors(
  segments: Segment[],
  mask: Uint8Array,
  width: number,
  height: number,
  footprint: Bounds,
  wallThickness: number,
  defaultWeight?: "heavy" | "light",
) {
  const clusters: Segment[][] = [];
  segments.forEach((segment) => {
    const cluster = clusters.find((candidate) => candidate[0].axis === segment.axis && Math.abs(candidate[0].line - segment.line) <= Math.max(3, wallThickness * 0.7));
    if (cluster) cluster.push(segment);
    else clusters.push([segment]);
  });

  const anchors: Segment[] = [];
  clusters.forEach((cluster) => {
    const axis = cluster[0].axis;
    const line = cluster.reduce((sum, segment) => sum + segment.line, 0) / cluster.length;
    const nearOuter = axis === "horizontal"
      ? Math.min(Math.abs(line - footprint.minY), Math.abs(line - footprint.maxY)) <= wallThickness * 2.2
      : Math.min(Math.abs(line - footprint.minX), Math.abs(line - footprint.maxX)) <= wallThickness * 2.2;
    const scanFrom = nearOuter
      ? (axis === "horizontal" ? footprint.minX : footprint.minY)
      : Math.min(...cluster.map((segment) => segment.from));
    const scanTo = nearOuter
      ? (axis === "horizontal" ? footprint.maxX : footprint.maxY)
      : Math.max(...cluster.map((segment) => segment.to));
    const thickness = nearOuter
      ? Math.max(wallThickness, ...cluster.map((segment) => segment.thickness))
      : Math.max(2, ...cluster.map((segment) => segment.thickness));
    const radius = Math.max(2, Math.round(thickness / 2));
    let start = -1;
    let lastSolid = -1;
    let gap = 0;
    const flush = () => {
      if (start < 0 || lastSolid - start + 1 < Math.max(4, wallThickness * 0.58)) return;
      anchors.push({ axis, line, from: start, to: lastSolid, thickness, density: 0.78, weight: defaultWeight });
    };

    for (let coordinate = Math.floor(scanFrom); coordinate <= Math.ceil(scanTo); coordinate += 1) {
      let dark = 0;
      let sampled = 0;
      for (let offset = -radius; offset <= radius; offset += 1) {
        const x = Math.round(axis === "horizontal" ? coordinate : line + offset);
        const y = Math.round(axis === "horizontal" ? line + offset : coordinate);
        if (x < 0 || x >= width || y < 0 || y >= height) continue;
        if (mask[y * width + x]) dark += 1;
        sampled += 1;
      }
      const solid = dark / Math.max(1, sampled) >= 0.48;
      if (solid) {
        if (start < 0) start = coordinate;
        lastSolid = coordinate;
        gap = 0;
      } else if (start >= 0) {
        gap += 1;
        if (gap > 1) {
          flush();
          start = -1;
          lastSolid = -1;
          gap = 0;
        }
      }
    }
    flush();
  });
  return mergeOverlaps([...segments, ...anchors]);
}

function longestPerpendicularRun(mask: Uint8Array, width: number, height: number, axis: Axis, coordinate: number, line: number, radius: number) {
  let longest = 0;
  for (let offset = -2; offset <= 2; offset += 1) {
    let current = 0;
    for (let delta = -radius; delta <= radius; delta += 1) {
      const x = Math.round(axis === "horizontal" ? coordinate + offset : line + delta);
      const y = Math.round(axis === "horizontal" ? line + delta : coordinate + offset);
      if (x < 0 || x >= width || y < 0 || y >= height) {
        current = 0;
      } else if (mask[y * width + x]) {
        current += 1;
        longest = Math.max(longest, current);
      } else current = 0;
    }
  }
  return longest;
}

function maskSupportedNear(mask: Uint8Array, width: number, height: number, x: number, y: number, radius: number) {
  const centerX = Math.round(x);
  const centerY = Math.round(y);
  const searchRadius = Math.max(1, Math.round(radius));
  for (let oy = -searchRadius; oy <= searchRadius; oy += 1) {
    for (let ox = -searchRadius; ox <= searchRadius; ox += 1) {
      const px = centerX + ox;
      const py = centerY + oy;
      if (px < 0 || px >= width || py < 0 || py >= height) continue;
      if (mask[py * width + px]) return 1;
    }
  }
  return 0;
}

/**
 * Recognize a conventional swing-door glyph in a wall gap. The model is local
 * to the wall: either gap end may be the hinge, the leaf may swing to either
 * side, and several opening angles/radii are tested. Requiring both the radial
 * leaf and the quarter-circle trace makes this substantially more selective
 * than using generic dark pixels near a gap.
 */
function doorSymbolScore(
  mask: Uint8Array,
  width: number,
  height: number,
  axis: Axis,
  line: number,
  from: number,
  to: number,
  thickness: number,
) {
  const gap = to - from;
  if (gap < Math.max(6, thickness * 0.9)) return { score: 0, leaf: 0, arc: 0 };
  const point = (along: number, normal: number): [number, number] => (
    axis === "horizontal" ? [along, line + normal] : [line + normal, along]
  );
  const tolerance = Math.max(1, thickness * 0.24);
  let best = { score: 0, leaf: 0, arc: 0 };

  for (const hingeAtStart of [true, false]) {
    const hinge = hingeAtStart ? from : to;
    const closedDirection = hingeAtStart ? 1 : -1;
    for (const side of [-1, 1]) {
      for (let openingDegrees = 55; openingDegrees <= 105; openingDegrees += 5) {
        const openingAngle = openingDegrees * Math.PI / 180;
        const leafAlongDirection = closedDirection * Math.cos(openingAngle);
        const leafNormalDirection = side * Math.sin(openingAngle);
        let leafHits = 0;
        let leafSamples = 0;
        for (let sample = 2; sample <= 12; sample += 1) {
          const radius = gap * sample / 12;
          const [x, y] = point(
            hinge + leafAlongDirection * radius,
            leafNormalDirection * radius,
          );
          leafHits += maskSupportedNear(mask, width, height, x, y, tolerance);
          leafSamples += 1;
        }
        const leafScore = leafHits / Math.max(1, leafSamples);

        let bestArcScore = 0;
        for (const radiusScale of [0.84, 0.92, 1]) {
          let arcHits = 0;
          let arcSamples = 0;
          for (let sample = 2; sample <= 14; sample += 1) {
            const angle = openingAngle * sample / 14;
            const radius = gap * radiusScale;
            const [x, y] = point(
              hinge + closedDirection * Math.cos(angle) * radius,
              side * Math.sin(angle) * radius,
            );
            arcHits += maskSupportedNear(mask, width, height, x, y, tolerance);
            arcSamples += 1;
          }
          bestArcScore = Math.max(bestArcScore, arcHits / Math.max(1, arcSamples));
        }

        // A real swing symbol has both features. Multiplication prevents a
        // nearby table edge or isolated curve from becoming a door on its own.
        const joint = Math.sqrt(leafScore * bestArcScore);
        const score = joint * 0.72 + Math.min(leafScore, bestArcScore) * 0.28;
        if (score > best.score) best = { score, leaf: leafScore, arc: bestArcScore };
      }
    }
  }
  return best;
}

export /**
 * A drawn window is normally two (sometimes three) parallel glazing lines
 * running the full width of the gap, close to the wall face. That produces
 * two narrow, near-solid density peaks a few pixels apart in the window
 * mask. A door's swing arc also lands inside the same mask, but a curve
 * only crosses any single offset briefly, so it never forms that tight,
 * near-solid pair. This is a stronger and more specific test than a single
 * "is there dark stuff nearby" density check, which a dimension-line
 * extension tick or a swing arc can both satisfy.
 */
function windowSymbolScore(
  windowMask: Uint8Array,
  width: number,
  height: number,
  axis: Axis,
  line: number,
  from: number,
  to: number,
  thickness: number,
) {
  const maxOffset = Math.max(6, Math.round(thickness * 2.5));
  const profile: number[] = [];
  for (let offset = -maxOffset; offset <= maxOffset; offset += 1) {
    let dark = 0;
    let sampled = 0;
    for (let coordinate = Math.ceil(from); coordinate <= Math.floor(to); coordinate += 1) {
      const x = Math.round(axis === "horizontal" ? coordinate : line + offset);
      const y = Math.round(axis === "horizontal" ? line + offset : coordinate);
      if (x < 0 || x >= width || y < 0 || y >= height) continue;
      if (windowMask[y * width + x]) dark += 1;
      sampled += 1;
    }
    profile.push(dark / Math.max(1, sampled));
  }
  const peakIndices: number[] = [];
  profile.forEach((density, index) => { if (density >= 0.82) peakIndices.push(index); });
  const maxSeparation = Math.max(8, thickness * 1.3);
  let bestPair = 0;
  for (let i = 0; i < peakIndices.length; i += 1) {
    for (let j = i + 1; j < peakIndices.length; j += 1) {
      const separation = peakIndices[j] - peakIndices[i];
      if (separation >= 1 && separation <= maxSeparation) {
        bestPair = Math.max(bestPair, Math.min(profile[peakIndices[i]], profile[peakIndices[j]]));
      }
    }
  }
  return bestPair;
}

function gapEvidence(
  mask: Uint8Array,
  windowMask: Uint8Array,
  width: number,
  height: number,
  axis: Axis,
  line: number,
  from: number,
  to: number,
  thickness: number,
  footprint: Bounds,
): GapEvidence {
  const gap = to - from;
  if (gap <= Math.max(3, thickness * 0.75)) return null;
  const symbol = doorSymbolScore(mask, width, height, axis, line, from, to, thickness);
  const radius = Math.max(5, Math.round(gap * 1.05));
  const perpendicular = Math.max(
    longestPerpendicularRun(mask, width, height, axis, from, line, radius),
    longestPerpendicularRun(mask, width, height, axis, to, line, radius),
  ) / gap;

  let windowParallel = 0;
  const lineRadius = Math.max(3, Math.round(thickness * 2.4));
  for (let offset = -lineRadius; offset <= lineRadius; offset += 1) {
    let dark = 0;
    let sampled = 0;
    for (let coordinate = Math.ceil(from); coordinate <= Math.floor(to); coordinate += 1) {
      const x = Math.round(axis === "horizontal" ? coordinate : line + offset);
      const y = Math.round(axis === "horizontal" ? line + offset : coordinate);
      if (x < 0 || x >= width || y < 0 || y >= height) continue;
      if (windowMask[y * width + x]) dark += 1;
      sampled += 1;
    }
    windowParallel = Math.max(windowParallel, dark / Math.max(1, sampled));
  }

  let arcDark = 0;
  let arcSampled = 0;
  const halfGap = Math.max(4, Math.round(gap));
  for (let major = -halfGap; major <= halfGap; major += 2) {
    for (let minor = Math.ceil(from); minor <= Math.floor(to); minor += 2) {
      const x = Math.round(axis === "horizontal" ? minor : line + major);
      const y = Math.round(axis === "horizontal" ? line + major : minor);
      if (x < 0 || x >= width || y < 0 || y >= height || Math.abs(major) <= thickness) continue;
      if (mask[y * width + x]) arcDark += 1;
      arcSampled += 1;
    }
  }
  const arcDensity = arcDark / Math.max(1, arcSampled);
  const outerTolerance = Math.max(4, thickness * 2.2);
  const onOuterWall = axis === "horizontal"
    ? Math.min(Math.abs(line - footprint.minY), Math.abs(line - footprint.maxY)) <= outerTolerance
    : Math.min(Math.abs(line - footprint.minX), Math.abs(line - footprint.maxX)) <= outerTolerance;
  const windowSymbol = windowSymbolScore(windowMask, width, height, axis, line, from, to, thickness);

  // A clean double-line glazing pair is decisive window evidence, but only on
  // an exterior wall: windows are uncommon in partitions, and closet
  // shelving can draw the same tight parallel pair. It only yields to a door
  // reading that is itself close to certain, which keeps a real swing door
  // safe (its arc never forms this tight parallel pair).
  if (onOuterWall && windowSymbol >= 0.8 && symbol.score < 0.85) {
    return { kind: "window", confidence: clamp(0.7 + windowSymbol * 0.25, 0.75, 0.96), evidence: "symbol" };
  }
  if (symbol.score >= 0.46 && symbol.leaf >= 0.42 && symbol.arc >= 0.34) {
    return {
      kind: "door",
      confidence: clamp(0.62 + symbol.score * 0.34, 0.68, 0.97),
      evidence: "symbol",
    };
  }
  if (onOuterWall && windowParallel >= 0.58) {
    return { kind: "window", confidence: clamp(0.62 + windowParallel * 0.3, 0.64, 0.94), evidence: "geometry" };
  }
  if (perpendicular >= 0.34 && arcDensity >= 0.006) {
    return { kind: "door", confidence: clamp(0.56 + perpendicular * 0.23 + arcDensity * 1.5, 0.58, 0.88), evidence: "geometry" };
  }
  const wallSpan = axis === "horizontal" ? footprint.maxX - footprint.minX : footprint.maxY - footprint.minY;
  if (!onOuterWall && gap <= wallSpan * 0.22 && (perpendicular >= 0.12 || arcDensity >= 0.003 || windowParallel >= 0.18)) {
    // Windows are very uncommon in partition walls. A bounded gap with either
    // a leaf, arc, or short cross-stroke is therefore stronger door evidence.
    return { kind: "door", confidence: clamp(0.57 + perpendicular * 0.18 + arcDensity + windowParallel * 0.08, 0.58, 0.82), evidence: "context" };
  }
  if (windowParallel >= 0.34 || (onOuterWall && windowParallel >= 0.17)) {
    return { kind: "window", confidence: clamp(0.55 + windowParallel * 0.4, 0.57, 0.93), evidence: "geometry" };
  }
  return null;
}

/**
 * Combine the traced-wall bounds with the ink envelope.
 *
 * The traced bounds are normally right and are kept: they sit on wall centre
 * lines, and the ink legitimately spills past the building wherever a balcony
 * rail, dimension chain or caption is drawn, so the ink envelope is routinely
 * the larger of the two by a healthy margin. The envelope is therefore only a
 * rescue for a footprint that has visibly collapsed, not a general correction.
 *
 * "Collapsed" is judged against the ink rather than against a pixel constant:
 * an axis that spans a small fraction of the ink, or a footprint whose area is
 * a small fraction of the ink's, is the tracer having failed on a thin or
 * colour-drawn plan. Ordinary balcony and annotation overshoot stays well
 * above these levels, so a healthy plan is left untouched.
 */
function reconcileFootprint(traced: Bounds, ink: Bounds | null): Bounds {
  if (!ink) return traced;
  const tracedWidth = Math.max(0, traced.maxX - traced.minX);
  const tracedHeight = Math.max(0, traced.maxY - traced.minY);
  const inkWidth = Math.max(1, ink.maxX - ink.minX);
  const inkHeight = Math.max(1, ink.maxY - ink.minY);
  const areaRatio = (tracedWidth * tracedHeight) / (inkWidth * inkHeight);
  const collapsedAxis = tracedWidth < inkWidth * 0.25 || tracedHeight < inkHeight * 0.25;
  if (areaRatio >= 0.45 && !collapsedAxis) return traced;
  return { ...ink };
}

function segmentBounds(segments: Segment[], fallback: Bounds): Bounds {
  if (!segments.length) return fallback;
  const horizontal = segments.filter((segment) => segment.axis === "horizontal");
  const vertical = segments.filter((segment) => segment.axis === "vertical");
  if (horizontal.length >= 2 && vertical.length >= 2) {
    return {
      minX: Math.min(...vertical.map((segment) => segment.line)),
      maxX: Math.max(...vertical.map((segment) => segment.line)),
      minY: Math.min(...horizontal.map((segment) => segment.line)),
      maxY: Math.max(...horizontal.map((segment) => segment.line)),
    };
  }
  let minX = Number.POSITIVE_INFINITY;
  let minY = Number.POSITIVE_INFINITY;
  let maxX = Number.NEGATIVE_INFINITY;
  let maxY = Number.NEGATIVE_INFINITY;
  segments.forEach((segment) => {
    if (segment.axis === "horizontal") {
      minX = Math.min(minX, segment.from);
      maxX = Math.max(maxX, segment.to);
      minY = Math.min(minY, segment.line);
      maxY = Math.max(maxY, segment.line);
    } else {
      minX = Math.min(minX, segment.line);
      maxX = Math.max(maxX, segment.line);
      minY = Math.min(minY, segment.from);
      maxY = Math.max(maxY, segment.to);
    }
  });
  return { minX, minY, maxX, maxY };
}

function railCandidates(segments: Segment[]) {
  const clusters: Segment[][] = [];
  [...segments].sort((a, b) => a.axis.localeCompare(b.axis) || a.line - b.line).forEach((segment) => {
    const cluster = clusters.find((candidate) => candidate[0].axis === segment.axis && Math.abs(candidate[0].line - segment.line) <= 3);
    if (cluster) cluster.push(segment);
    else clusters.push([segment]);
  });
  return clusters.map((cluster) => {
    const totalLength = cluster.reduce((sum, segment) => sum + segment.to - segment.from + 1, 0);
    return {
      axis: cluster[0].axis,
      line: cluster.reduce((sum, segment) => sum + segment.line * (segment.to - segment.from + 1), 0) / totalLength,
      from: Math.min(...cluster.map((segment) => segment.from)),
      to: Math.max(...cluster.map((segment) => segment.to)),
      thickness: Math.max(...cluster.map((segment) => segment.thickness)),
      density: cluster.reduce((sum, segment) => sum + segment.density, 0) / cluster.length,
    } satisfies Segment;
  });
}

function buildWalls(
  segments: Segment[],
  mask: Uint8Array,
  windowMask: Uint8Array,
  width: number,
  height: number,
  wallThickness: number,
  footprint: Bounds,
) {
  const clusters: Segment[][] = [];
  [...segments].sort((a, b) => a.axis.localeCompare(b.axis) || a.line - b.line || a.from - b.from).forEach((segment) => {
    const cluster = clusters.find((candidate) => {
      const reference = candidate[0];
      return reference.axis === segment.axis && Math.abs(reference.line - segment.line) <= Math.max(3, wallThickness * 0.7);
    });
    if (cluster) cluster.push(segment);
    else clusters.push([segment]);
  });

  const walls: DetectedWall[] = [];
  clusters.forEach((cluster) => {
    const axis = cluster[0].axis;
    const sorted = [...cluster].sort((a, b) => a.from - b.from);
    let wallStart = sorted[0].from;
    let wallEnd = sorted[0].to;
    let weightedLine = sorted[0].line;
    let totalWeight = sorted[0].to - sorted[0].from + 1;
    let thickness = sorted[0].thickness;
    let openings: DetectedOpening[] = [];
    let hasHeavySegment = sorted[0].weight !== "light";

    const finish = () => {
      if (wallEnd - wallStart < 4) return;
      const line = weightedLine;
      const start: [number, number] = axis === "horizontal" ? [wallStart, line] : [line, wallStart];
      const end: [number, number] = axis === "horizontal" ? [wallEnd, line] : [line, wallEnd];
      walls.push({
        id: `detected-wall-${walls.length + 1}`,
        axis,
        start,
        end,
        thickness,
        confidence: clamp(0.66 + Math.min(0.2, (wallEnd - wallStart) / 900) + Math.min(0.08, thickness / 80), 0.64, 0.94),
        openings,
        weight: hasHeavySegment ? "heavy" : "light",
      });
    };

    for (let index = 1; index < sorted.length; index += 1) {
      const segment = sorted[index];
      if (segment.from <= wallEnd + 2) {
        const weight = segment.to - segment.from + 1;
        weightedLine = (weightedLine * totalWeight + segment.line * weight) / (totalWeight + weight);
        totalWeight += weight;
        wallEnd = Math.max(wallEnd, segment.to);
        thickness = Math.max(thickness, segment.thickness);
        hasHeavySegment = hasHeavySegment || segment.weight !== "light";
        continue;
      }
      const evidence = gapEvidence(mask, windowMask, width, height, axis, weightedLine, wallEnd, segment.from, thickness, footprint);
      const maximumOpening = Math.max(18, (axis === "horizontal" ? footprint.maxX - footprint.minX : footprint.maxY - footprint.minY) * 0.34);
      if (evidence && segment.from - wallEnd <= maximumOpening) {
        openings.push({
          kind: evidence.kind,
          offset: wallEnd - wallStart,
          width: segment.from - wallEnd,
          confidence: evidence.confidence,
          evidence: evidence.evidence,
        });
        const weight = segment.to - segment.from + 1;
        weightedLine = (weightedLine * totalWeight + segment.line * weight) / (totalWeight + weight);
        totalWeight += weight;
        wallEnd = segment.to;
        thickness = Math.max(thickness, segment.thickness);
        hasHeavySegment = hasHeavySegment || segment.weight !== "light";
      } else {
        finish();
        wallStart = segment.from;
        wallEnd = segment.to;
        weightedLine = segment.line;
        totalWeight = segment.to - segment.from + 1;
        thickness = segment.thickness;
        openings = [];
        hasHeavySegment = segment.weight !== "light";
      }
    }
    finish();
  });
  return walls;
}

/**
 * Browser resampling can inflate a one-pixel dimension line until the early
 * run detector merges it with a genuine wall. Validate the final wall's
 * perpendicular thickness and trim only a single unsupported interior tail.
 * Exterior walls and walls containing openings are intentionally untouched.
 */
function trimUnsupportedInteriorWallTails(
  walls: DetectedWall[],
  mask: Uint8Array,
  width: number,
  height: number,
  minimumRun: number,
  footprint: Bounds,
): DetectedWall[] {
  return walls.map((wall) => {
    const from = wall.axis === "horizontal" ? wall.start[0] : wall.start[1];
    const to = wall.axis === "horizontal" ? wall.end[0] : wall.end[1];
    const line = wall.axis === "horizontal" ? wall.start[1] : wall.start[0];
    const footprintStart = wall.axis === "horizontal" ? footprint.minY : footprint.minX;
    const footprintEnd = wall.axis === "horizontal" ? footprint.maxY : footprint.maxX;
    const length = to - from + 1;
    const exteriorTolerance = Math.max(5, wall.thickness * 1.5);
    if (
      wall.openings.length
      || wall.thickness < 4
      || length < minimumRun * 5
      || Math.min(Math.abs(line - footprintStart), Math.abs(line - footprintEnd)) <= exteriorTolerance
    ) return wall;

    const radius = Math.max(4, Math.ceil(wall.thickness));
    const crossSections: number[] = [];
    for (let coordinate = Math.floor(from); coordinate <= Math.ceil(to); coordinate += 1) {
      crossSections.push(longestPerpendicularRun(mask, width, height, wall.axis, coordinate, line, radius));
    }
    const ranked = [...crossSections].sort((a, b) => a - b);
    const baseline = ranked[Math.floor((ranked.length - 1) * 0.88)] ?? 0;
    if (baseline < 4) return wall;

    // Use the reconstructed wall thickness as the ceiling. Intersections and
    // text glyphs can dominate a high percentile even when the sustained wall
    // body is consistently four pixels thick.
    const minimumCrossSection = Math.max(3, Math.min(Math.ceil(wall.thickness), Math.ceil(baseline * 0.78)));
    const gapTolerance = Math.max(1, Math.round(wall.thickness * 0.35));
    const runs: Array<{ from: number; to: number }> = [];
    let runStart = -1;
    let lastSupported = -1;
    let gap = 0;
    const flush = () => {
      if (runStart < 0 || lastSupported - runStart + 1 < Math.max(8, minimumRun, wall.thickness * 1.6)) return;
      runs.push({ from: runStart, to: lastSupported });
    };

    crossSections.forEach((crossSection, index) => {
      if (crossSection >= minimumCrossSection) {
        if (runStart < 0) runStart = index;
        lastSupported = index;
        gap = 0;
      } else if (runStart >= 0) {
        gap += 1;
        if (gap > gapTolerance) {
          flush();
          runStart = -1;
          lastSupported = -1;
          gap = 0;
        }
      }
    });
    flush();
    if (runs.length !== 1) return wall;

    const supported = runs[0];
    const supportedFrom = from + supported.from;
    const supportedTo = from + supported.to;
    const edgeTolerance = Math.max(4, wall.thickness * 1.4);
    const touchesStart = supportedFrom <= from + edgeTolerance;
    const touchesEnd = supportedTo >= to - edgeTolerance;
    const trimmedLength = length - (supportedTo - supportedFrom + 1);
    if (touchesStart === touchesEnd || trimmedLength < Math.max(minimumRun * 2, length * 0.25)) return wall;

    // The retained end must terminate at a perpendicular wall. This prevents
    // trimming a legitimate wall merely because one half is drawn more faintly.
    const retainedJoint = touchesStart ? supportedTo : supportedFrom;
    const hasPerpendicularJoint = walls.some((candidate) => candidate !== wall && candidate.axis !== wall.axis && (
      wall.axis === "horizontal"
        ? Math.abs(candidate.start[0] - retainedJoint) <= edgeTolerance
          && candidate.start[1] <= line + edgeTolerance
          && candidate.end[1] >= line - edgeTolerance
        : Math.abs(candidate.start[1] - retainedJoint) <= edgeTolerance
          && candidate.start[0] <= line + edgeTolerance
          && candidate.end[0] >= line - edgeTolerance
    ));
    if (!hasPerpendicularJoint) return wall;

    return {
      ...wall,
      start: wall.axis === "horizontal" ? [supportedFrom, line] : [line, supportedFrom],
      end: wall.axis === "horizontal" ? [supportedTo, line] : [line, supportedTo],
      confidence: Math.min(wall.confidence, 0.86),
    };
  });
}

function recoverEmbeddedOpenings(
  walls: DetectedWall[],
  strongMask: Uint8Array,
  mediumMask: Uint8Array,
  windowMask: Uint8Array,
  width: number,
  height: number,
  footprint: Bounds,
) {
  return walls.map((wall) => {
    const from = wall.axis === "horizontal" ? wall.start[0] : wall.start[1];
    const to = wall.axis === "horizontal" ? wall.end[0] : wall.end[1];
    const line = wall.axis === "horizontal" ? wall.start[1] : wall.start[0];
    const outerTolerance = Math.max(4, wall.thickness * 2.2);
    const onOuterWall = wall.axis === "horizontal"
      ? Math.min(Math.abs(line - footprint.minY), Math.abs(line - footprint.maxY)) <= outerTolerance
      : Math.min(Math.abs(line - footprint.minX), Math.abs(line - footprint.maxX)) <= outerTolerance;
    const scanFrom = Math.max(from, wall.axis === "horizontal" ? footprint.minX : footprint.minY);
    const scanTo = Math.min(to, wall.axis === "horizontal" ? footprint.maxX : footprint.maxY);
    const radius = Math.max(2, Math.round(wall.thickness / 2));
    const span = scanTo - scanFrom;
    const openings = [...wall.openings];
    let gapStart = -1;
    let lastGap = -1;
    let solidRun = 0;

    const flush = () => {
      if (gapStart < 0) return;
      const gapWidth = lastGap - gapStart + 1;
      const minimumGap = Math.max(6, wall.thickness * 0.82);
      if (gapWidth < minimumGap || gapWidth > span * 0.34) return;
      const overlapsKnown = openings.some((opening) => {
        const knownFrom = from + opening.offset;
        const knownTo = knownFrom + opening.width;
        return Math.max(0, Math.min(lastGap, knownTo) - Math.max(gapStart, knownFrom)) >= Math.min(gapWidth, opening.width) * 0.45;
      });
      if (overlapsKnown) return;
      const evidence = gapEvidence(mediumMask, windowMask, width, height, wall.axis, line, gapStart, lastGap, wall.thickness, footprint);
      if (!evidence) return;
      if (onOuterWall && evidence.kind === "door" && evidence.confidence < 0.7) return;
      openings.push({
        kind: evidence.kind,
        offset: gapStart - from,
        width: gapWidth,
        confidence: Math.max(0.56, evidence.confidence - 0.03),
        evidence: evidence.evidence,
      });
    };

    // Solidity is measured as a contiguous ink core relative to the wall's own
    // thickness, for the same reason as splitByStrokeSupport: counting ink in
    // a fixed window sized from the wall's widest point reports a wall that
    // tapers by a pixel as a run of narrow gaps, which then get classified as
    // a row of spurious doorways.
    const minimumCore = Math.max(2, wall.thickness * 0.5);
    for (let coordinate = Math.floor(scanFrom); coordinate <= Math.ceil(scanTo); coordinate += 1) {
      let longest = 0;
      let run = 0;
      for (let offset = -radius; offset <= radius; offset += 1) {
        const x = Math.round(wall.axis === "horizontal" ? coordinate : line + offset);
        const y = Math.round(wall.axis === "horizontal" ? line + offset : coordinate);
        if (x < 0 || x >= width || y < 0 || y >= height) {
          run = 0;
          continue;
        }
        if (strongMask[y * width + x]) {
          run += 1;
          if (run > longest) longest = run;
        } else run = 0;
      }
      const solid = longest >= minimumCore;
      if (!solid) {
        if (gapStart < 0) gapStart = coordinate;
        lastGap = coordinate;
        solidRun = 0;
      } else if (gapStart >= 0) {
        solidRun += 1;
        if (solidRun > 1) {
          lastGap = coordinate - solidRun;
          flush();
          gapStart = -1;
          lastGap = -1;
          solidRun = 0;
        }
      }
    }
    return { ...wall, openings: openings.sort((a, b) => a.offset - b.offset) };
  });
}

function overlapRatio(from: number, to: number, targetFrom: number, targetTo: number) {
  return Math.max(0, Math.min(to, targetTo) - Math.max(from, targetFrom)) / Math.max(1, targetTo - targetFrom);
}

function detectOutdoorAreas(rawSegments: Segment[], footprint: Bounds, wallThickness: number) {
  const railsAndSupports = railCandidates(rawSegments);
  const footprintWidth = footprint.maxX - footprint.minX;
  const footprintHeight = footprint.maxY - footprint.minY;
  const result: DetectedOutdoorArea[] = [];

  const addHorizontalArea = (side: "top" | "bottom") => {
    const edge = side === "top" ? footprint.minY : footprint.maxY;
    const direction = side === "top" ? -1 : 1;
    const rails = railsAndSupports.filter((segment) => {
      if (segment.axis !== "horizontal") return false;
      const depth = (segment.line - edge) * direction;
      return depth >= footprintHeight * 0.1
        && depth <= footprintHeight * 0.62
        && overlapRatio(segment.from, segment.to, footprint.minX, footprint.maxX) >= 0.30;
    }).sort((a, b) => Math.abs(a.line - edge) - Math.abs(b.line - edge));

    for (const rail of rails) {
      const depth = Math.abs(rail.line - edge);
      const sideSupports = railsAndSupports.filter((segment) => (
        segment.axis === "vertical"
        && segment.to - segment.from >= depth * 0.55
        && (
          Math.min(Math.abs(segment.line - footprint.minX), Math.abs(segment.line - footprint.maxX)) <= wallThickness * 2.2
          || (segment.line >= rail.from - wallThickness * 3 && segment.line <= rail.to + wallThickness * 3)
        )
        && Math.min(Math.abs(segment.from - Math.min(edge, rail.line)), Math.abs(segment.to - Math.max(edge, rail.line))) <= wallThickness * 4
      ));
      if (sideSupports.length < 2) continue;
      const supportXs = sideSupports.map((support) => support.line).sort((a, b) => a - b);
      const minX = supportXs[0];
      const maxX = supportXs.at(-1) ?? rail.to;
      if (maxX - minX < footprintWidth * 0.4) continue;
      result.push({
        id: `outdoor-${side}-${result.length + 1}`,
        side,
        x: minX,
        y: Math.min(edge, rail.line),
        width: maxX - minX,
        height: depth,
        confidence: clamp(0.64 + Math.min(0.16, sideSupports.length * 0.04) + overlapRatio(rail.from, rail.to, footprint.minX, footprint.maxX) * 0.13, 0.65, 0.92),
      });
      break;
    }
  };

  const addVerticalArea = (side: "left" | "right") => {
    const edge = side === "left" ? footprint.minX : footprint.maxX;
    const direction = side === "left" ? -1 : 1;
    const rails = railsAndSupports.filter((segment) => {
      if (segment.axis !== "vertical") return false;
      const depth = (segment.line - edge) * direction;
      return depth >= footprintWidth * 0.1
        && depth <= footprintWidth * 0.62
        && overlapRatio(segment.from, segment.to, footprint.minY, footprint.maxY) >= 0.44;
    }).sort((a, b) => Math.abs(a.line - edge) - Math.abs(b.line - edge));

    for (const rail of rails) {
      const depth = Math.abs(rail.line - edge);
      const sideSupports = railsAndSupports.filter((segment) => (
        segment.axis === "horizontal"
        && segment.to - segment.from >= depth * 0.55
        && (
          Math.min(Math.abs(segment.line - footprint.minY), Math.abs(segment.line - footprint.maxY)) <= wallThickness * 2.2
          || (segment.line >= rail.from - wallThickness * 3 && segment.line <= rail.to + wallThickness * 3)
        )
      ));
      if (sideSupports.length < 2) continue;
      const supportYs = sideSupports.map((support) => support.line).sort((a, b) => a - b);
      const minY = supportYs[0];
      const maxY = supportYs.at(-1) ?? rail.to;
      if (maxY - minY < footprintHeight * 0.4) continue;
      result.push({
        id: `outdoor-${side}-${result.length + 1}`,
        side,
        x: Math.min(edge, rail.line),
        y: minY,
        width: depth,
        height: maxY - minY,
        confidence: clamp(0.64 + Math.min(0.16, sideSupports.length * 0.04) + overlapRatio(rail.from, rail.to, footprint.minY, footprint.maxY) * 0.13, 0.65, 0.92),
      });
      break;
    }
  };

  addHorizontalArea("top");
  addHorizontalArea("bottom");
  addVerticalArea("left");
  addVerticalArea("right");
  return result.sort((a, b) => b.confidence - a.confidence).slice(0, 2);
}

type StairBox = Bounds & { runAxis: Axis; railCount: number };

function consolidateStairRails(rawSegments: Segment[], minimumDimension: number, wallThickness: number) {
  // Compressed screenshots often preserve only short fragments of one stair
  // stringer; the paired-rail and cross-stroke checks below provide the guard
  // against admitting arbitrary short lines.
  const minimumLength = minimumDimension * 0.052;
  const maximumLength = minimumDimension * 0.52;
  const maximumThickness = Math.max(4, wallThickness * 0.82);
  const maximumGap = minimumDimension * 0.16;
  const rails = rawSegments
    .filter((segment) => (
      segment.thickness <= maximumThickness
      && segment.to - segment.from >= minimumLength
      && segment.to - segment.from <= maximumLength
    ))
    .sort((a, b) => a.axis.localeCompare(b.axis) || a.line - b.line || a.from - b.from);
  const result: Segment[] = [];

  rails.forEach((rail) => {
    const existing = result.find((candidate) => (
      candidate.axis === rail.axis
      && Math.abs(candidate.line - rail.line) <= Math.max(2, wallThickness * 0.58)
      && rail.from <= candidate.to + maximumGap
      && rail.to >= candidate.from - maximumGap
    ));
    if (!existing) {
      result.push({ ...rail });
      return;
    }
    const existingLength = existing.to - existing.from;
    const railLength = rail.to - rail.from;
    existing.line = (existing.line * existingLength + rail.line * railLength) / Math.max(1, existingLength + railLength);
    existing.from = Math.min(existing.from, rail.from);
    existing.to = Math.max(existing.to, rail.to);
    existing.thickness = Math.min(existing.thickness, rail.thickness);
    existing.density = Math.max(existing.density, rail.density);
  });
  return result;
}

function mergeStairBoxes(boxes: StairBox[], padding: number) {
  const merged: StairBox[] = [];
  boxes.forEach((box) => {
    const target = merged.find((candidate) => {
      if (candidate.runAxis !== box.runAxis) return false;
      const crossAdjacent = box.runAxis === "vertical"
        ? Math.min(Math.abs(candidate.maxX - box.minX), Math.abs(box.maxX - candidate.minX)) <= padding
        : Math.min(Math.abs(candidate.maxY - box.minY), Math.abs(box.maxY - candidate.minY)) <= padding;
      const runOverlap = box.runAxis === "vertical"
        ? Math.min(candidate.maxY, box.maxY) - Math.max(candidate.minY, box.minY)
        : Math.min(candidate.maxX, box.maxX) - Math.max(candidate.minX, box.minX);
      return crossAdjacent && runOverlap > 0;
    });
    if (!target) {
      merged.push({ ...box });
      return;
    }
    target.minX = Math.min(target.minX, box.minX);
    target.minY = Math.min(target.minY, box.minY);
    target.maxX = Math.max(target.maxX, box.maxX);
    target.maxY = Math.max(target.maxY, box.maxY);
    target.railCount += box.railCount;
  });
  return merged;
}

function stairCrossStrokeCenters(
  mask: Uint8Array,
  width: number,
  height: number,
  box: StairBox,
  wallThickness: number,
) {
  const horizontalRun = box.runAxis === "horizontal";
  const majorStart = Math.max(0, Math.ceil(horizontalRun ? box.minX : box.minY));
  const majorEnd = Math.min(horizontalRun ? width - 1 : height - 1, Math.floor(horizontalRun ? box.maxX : box.maxY));
  const minorStart = Math.max(0, Math.ceil(horizontalRun ? box.minY : box.minX) + 2);
  const minorEnd = Math.min(horizontalRun ? height - 1 : width - 1, Math.floor(horizontalRun ? box.maxY : box.maxX) - 2);
  const span = Math.max(1, minorEnd - minorStart + 1);
  const active: boolean[] = [];

  for (let major = majorStart; major <= majorEnd; major += 1) {
    let dark = 0;
    let longest = 0;
    let run = 0;
    let onePixelGap = 0;
    for (let minor = minorStart; minor <= minorEnd; minor += 1) {
      const x = horizontalRun ? major : minor;
      const y = horizontalRun ? minor : major;
      if (mask[y * width + x]) {
        dark += 1;
        run += 1 + onePixelGap;
        onePixelGap = 0;
        longest = Math.max(longest, run);
      } else if (run > 0 && onePixelGap === 0) {
        onePixelGap = 1;
      } else {
        run = 0;
        onePixelGap = 0;
      }
    }
    active.push(dark / span >= 0.2 && longest / span >= 0.28);
  }

  const centers: number[] = [];
  let start = -1;
  active.forEach((value, index) => {
    if (value && start < 0) start = index;
    if ((!value || index === active.length - 1) && start >= 0) {
      const end = value && index === active.length - 1 ? index : index - 1;
      const thickness = end - start + 1;
      if (thickness <= Math.max(5, wallThickness * 0.9)) centers.push(majorStart + (start + end) / 2);
      start = -1;
    }
  });
  return centers;
}

export function expandDetectedStairReturn(
  stair: DetectedStair,
  mask: Uint8Array,
  width: number,
  height: number,
  footprint: { x: number; y: number; width: number; height: number },
  wallThickness: number,
) {
  const vertical = stair.runAxis === "vertical";
  const crossLength = vertical ? stair.width : stair.height;
  const runLength = vertical ? stair.height : stair.width;
  const stairCrossCenter = vertical ? stair.x + stair.width / 2 : stair.y + stair.height / 2;
  const footprintCrossCenter = vertical ? footprint.x + footprint.width / 2 : footprint.y + footprint.height / 2;
  // A complete half-turn shaft is normally centered even when the parallel
  // flight detected from its regular treads is not. Avoid altering already
  // centered shaft detections.
  if (Math.abs(stairCrossCenter - footprintCrossCenter) <= crossLength * 0.12) return stair;

  const direction = stairCrossCenter > footprintCrossCenter ? -1 : 1;
  const footprintCrossLength = vertical ? footprint.width : footprint.height;
  const maximumDistance = Math.floor(Math.min(crossLength * 0.8, runLength * 0.5, footprintCrossLength * 0.16));
  const runStart = Math.round((vertical ? stair.y : stair.x) + runLength * 0.09);
  const runEnd = Math.round((vertical ? stair.y + stair.height : stair.x + stair.width) - runLength * 0.09);
  const boundary = direction < 0
    ? Math.floor(vertical ? stair.x : stair.y) - 1
    : Math.ceil(vertical ? stair.x + stair.width : stair.y + stair.height) + 1;
  const gapAllowance = Math.max(2, Math.ceil(wallThickness * 0.48));
  const span = Math.max(1, runEnd - runStart + 1);
  let gaps = 0;
  let farthestEvidence = 0;

  for (let distance = 1; distance <= maximumDistance; distance += 1) {
    const cross = boundary + direction * (distance - 1);
    let hits = 0;
    for (let run = runStart; run <= runEnd; run += 1) {
      const x = vertical ? cross : run;
      const y = vertical ? run : cross;
      if (x >= 0 && x < width && y >= 0 && y < height && mask[y * width + x]) hits += 1;
    }
    // Return flights and winders contribute short connected strokes. Solid
    // wall columns/rows are deliberately rejected here.
    const active = hits >= 2 && hits <= span * 0.52;
    if (active) {
      farthestEvidence = distance;
      gaps = 0;
    } else {
      gaps += 1;
      if (gaps > gapAllowance) break;
    }
  }

  const minimumExpansion = Math.max(3, crossLength * 0.16);
  const maximumShaftCross = footprintCrossLength * 0.25;
  const expansion = Math.min(farthestEvidence, Math.max(0, maximumShaftCross - crossLength));
  if (expansion < minimumExpansion) return stair;
  if (vertical) {
    return {
      ...stair,
      x: direction < 0 ? stair.x - expansion : stair.x,
      width: stair.width + expansion,
      confidence: Math.min(0.92, stair.confidence + 0.04),
    };
  }
  return {
    ...stair,
    y: direction < 0 ? stair.y - expansion : stair.y,
    height: stair.height + expansion,
    confidence: Math.min(0.92, stair.confidence + 0.04),
  };
}

/**
 * Detect the ascent direction of a stair from its direction arrow.
 *
 * Architectural plans draw a thin shaft line along the run centerline with a
 * filled or open arrowhead at the "up" end. Strategy:
 *   1. Scan the run-axis centerline inside the stair box for a single thin
 *      (1–4px) continuous or near-continuous stroke — the arrow shaft.
 *   2. At each end of the shaft, count ink in a small fan to detect whether
 *      the stroke widens into an arrowhead (V shape). The end with more
 *      diagonal ink is the "up" direction.
 *
 * Returns "start" (low run-coord end goes up) or "end" (high run-coord end
 * goes up), or null when the evidence is too weak to trust.
 */
function detectStairAscendDirection(
  mask: Uint8Array,
  width: number,
  height: number,
  stair: { x: number; y: number; width: number; height: number; runAxis: Axis },
): "start" | "end" | null {
  const vertical = stair.runAxis === "vertical";
  // Run coordinate range and cross-axis center
  const runMin = Math.round(vertical ? stair.y : stair.x) + 2;
  const runMax = Math.round(vertical ? stair.y + stair.height : stair.x + stair.width) - 2;
  const crossCenter = Math.round(vertical ? stair.x + stair.width / 2 : stair.y + stair.height / 2);
  const crossRange = Math.round((vertical ? stair.width : stair.height) * 0.18);
  if (runMax - runMin < 10) return null;

  // Count ink on the centerline (within crossRange either side) for each run coordinate
  const inkAt = (run: number) => {
    let count = 0;
    for (let offset = -crossRange; offset <= crossRange; offset += 1) {
      const x = vertical ? crossCenter + offset : run;
      const y = vertical ? run : crossCenter + offset;
      if (x >= 0 && x < width && y >= 0 && y < height && mask[y * width + x]) count += 1;
    }
    return count;
  };

  // Count how many run positions near each end have ink (shaft presence)
  const probeSpan = Math.max(4, Math.round((runMax - runMin) * 0.12));
  let startInk = 0;
  let endInk = 0;
  for (let i = 0; i < probeSpan; i += 1) {
    startInk += inkAt(runMin + i);
    endInk += inkAt(runMax - i);
  }

  // Count diagonal (angled) strokes near each end — arrowhead signal.
  // An arrowhead fans outward, so it has ink OUTSIDE the center cross-range.
  const arrowProbeRun = Math.round((runMax - runMin) * 0.1);
  const arrowCrossMin = crossRange + 1;
  const arrowCrossMax = Math.round((vertical ? stair.width : stair.height) * 0.42);
  const countDiagonalInk = (runEdge: number, runDir: number) => {
    let count = 0;
    for (let dr = 0; dr < arrowProbeRun; dr += 1) {
      const run = runEdge + dr * runDir;
      for (let offset = -arrowCrossMax; offset <= arrowCrossMax; offset += 1) {
        const absOffset = Math.abs(offset);
        if (absOffset < arrowCrossMin) continue;
        const x = vertical ? crossCenter + offset : run;
        const y = vertical ? run : crossCenter + offset;
        if (x >= 0 && x < width && y >= 0 && y < height && mask[y * width + x]) count += 1;
      }
    }
    return count;
  };

  const startArrow = countDiagonalInk(runMin, 1);   // diagonal ink near the start end
  const endArrow = countDiagonalInk(runMax, -1);    // diagonal ink near the end end

  // Require that: (a) there's a clear center-line shaft on at least one side,
  // (b) one end has meaningfully more arrowhead ink than the other.
  const shaftPresent = Math.max(startInk, endInk) >= probeSpan * crossRange * 0.18;
  if (!shaftPresent) return null;

  const arrowDiff = Math.abs(endArrow - startArrow);
  const arrowTotal = startArrow + endArrow;
  if (arrowTotal < 4 || arrowDiff < arrowTotal * 0.28) return null; // not decisive

  // The end with MORE diagonal ink is the arrowhead = the "up" end
  return endArrow > startArrow ? "end" : "start";
}

function detectStairs(
  rawSegments: Segment[],
  mediumMask: Uint8Array,
  width: number,
  height: number,
  footprint: Bounds,
  minimumDimension: number,
  wallThickness: number,
) {
  const rails = consolidateStairRails(rawSegments, minimumDimension, wallThickness);
  const minimumSeparation = minimumDimension * 0.065;
  const maximumSeparation = minimumDimension * 0.31;
  const minimumOverlap = minimumDimension * 0.045;
  const boxes: StairBox[] = [];

  rails.forEach((first, firstIndex) => {
    rails.slice(firstIndex + 1).forEach((second) => {
      if (first.axis !== second.axis) return;
      const separation = Math.abs(second.line - first.line);
      const overlap = Math.max(0, Math.min(first.to, second.to) - Math.max(first.from, second.from));
      if (separation < minimumSeparation || separation > maximumSeparation || overlap < minimumOverlap) return;
      const runFrom = Math.min(first.from, second.from);
      const runTo = Math.max(first.to, second.to);
      const runLength = runTo - runFrom;
      if (runLength < separation * 0.52 || runLength > separation * 4.8) return;

      const box: StairBox = first.axis === "vertical"
        ? { minX: Math.min(first.line, second.line), maxX: Math.max(first.line, second.line), minY: runFrom, maxY: runTo, runAxis: "vertical", railCount: 2 }
        : { minX: runFrom, maxX: runTo, minY: Math.min(first.line, second.line), maxY: Math.max(first.line, second.line), runAxis: "horizontal", railCount: 2 };
      const centerX = (box.minX + box.maxX) / 2;
      const centerY = (box.minY + box.maxY) / 2;
      if (centerX < footprint.minX - wallThickness * 2 || centerX > footprint.maxX + wallThickness * 2) return;
      if (centerY < footprint.minY - wallThickness * 2 || centerY > footprint.maxY + wallThickness * 2) return;
      boxes.push(box);
    });
  });

  const footprintMinimum = Math.min(footprint.maxX - footprint.minX, footprint.maxY - footprint.minY);
  const validatedBoxes = boxes.filter((box) => {
    const boxWidth = box.maxX - box.minX;
    const boxHeight = box.maxY - box.minY;
    const crossLength = box.runAxis === "vertical" ? boxWidth : boxHeight;
    if (crossLength > footprintMinimum * 0.32) return false;
    return stairCrossStrokeCenters(mediumMask, width, height, box, wallThickness).length >= 2;
  });

  const detected = mergeStairBoxes(validatedBoxes, Math.max(3, wallThickness * 0.75))
    .flatMap((box, index): DetectedStair[] => {
      const centers = stairCrossStrokeCenters(mediumMask, width, height, box, wallThickness);
      if (centers.length < 2) return [];
      const runLength = box.runAxis === "vertical" ? box.maxY - box.minY : box.maxX - box.minX;
      const gaps = centers.slice(1).map((center, gapIndex) => center - centers[gapIndex]).filter((gap) => gap >= 2);
      const sortedGaps = [...gaps].sort((a, b) => a - b);
      const medianGap = sortedGaps[Math.floor(sortedGaps.length / 2)] ?? runLength / 7;
      const stepCount = clamp(Math.round(runLength / Math.max(3, medianGap)), Math.max(5, centers.length), 16);
      if (stepCount < 6) return [];
      const boxWidth = box.maxX - box.minX;
      const boxHeight = box.maxY - box.minY;
      const crossLength = box.runAxis === "vertical" ? boxWidth : boxHeight;
      if (crossLength > footprintMinimum * 0.32) return [];
      if (boxWidth > (footprint.maxX - footprint.minX) * 0.42 || boxHeight > (footprint.maxY - footprint.minY) * 0.54) return [];
      const candidate: DetectedStair = {
        id: `stair-${index + 1}`,
        runAxis: box.runAxis,
        x: box.minX,
        y: box.minY,
        width: boxWidth,
        height: boxHeight,
        stepCount,
        confidence: clamp(0.58 + Math.min(0.2, centers.length * 0.035) + Math.min(0.08, (box.railCount - 2) * 0.02), 0.62, 0.9),
      };
      const ascend = detectStairAscendDirection(mediumMask, width, height, candidate);
      if (ascend) candidate.ascend = ascend;
      return [candidate];
    })
    .sort((a, b) => b.confidence - a.confidence);
  const expanded = detected.map((stair) => expandDetectedStairReturn(stair, mediumMask, width, height, {
    x: footprint.minX,
    y: footprint.minY,
    width: footprint.maxX - footprint.minX,
    height: footprint.maxY - footprint.minY,
  }, wallThickness));
  const unique: DetectedStair[] = [];
  expanded.forEach((stair) => {
    const duplicate = unique.some((candidate) => {
      if (candidate.runAxis !== stair.runAxis) return false;
      const intersectionWidth = Math.max(0, Math.min(candidate.x + candidate.width, stair.x + stair.width) - Math.max(candidate.x, stair.x));
      const intersectionHeight = Math.max(0, Math.min(candidate.y + candidate.height, stair.y + stair.height) - Math.max(candidate.y, stair.y));
      const intersection = intersectionWidth * intersectionHeight;
      return intersection / Math.max(1, Math.min(candidate.width * candidate.height, stair.width * stair.height)) >= 0.72;
    });
    if (!duplicate) unique.push(stair);
  });
  return unique.slice(0, 2).map((stair, index) => ({ ...stair, id: `stair-${index + 1}` }));
}

/**
 * Rooms are a topology estimate, not semantic recognition: walls are
 * rasterized onto a grid, the outer border is closed, and flood fill finds
 * enclosed empty components. An opening never closes a gap in the wall
 * rasterization, so a doorway does not merge two rooms into one. Each room's
 * shape is reported as the bounding box of its cells rather than a traced
 * outline: exact for the rectangular rooms typical of a residential plan,
 * an overestimate for an L-shaped one.
 */
function detectRooms(walls: DetectedWall[], footprint: Bounds): DetectedRoom[] {
  if (walls.length < 4) return [];
  const gridSize = 72;
  const grid = new Uint8Array(gridSize * gridSize);
  const spanX = Math.max(1, footprint.maxX - footprint.minX);
  const spanY = Math.max(1, footprint.maxY - footprint.minY);
  const toGrid = (x: number, y: number) => [
    clamp(Math.round(((x - footprint.minX) / spanX) * (gridSize - 5)) + 2, 0, gridSize - 1),
    clamp(Math.round(((y - footprint.minY) / spanY) * (gridSize - 5)) + 2, 0, gridSize - 1),
  ] as const;
  const toPixel = (gx: number, gy: number): [number, number] => [
    footprint.minX + ((gx - 2) / (gridSize - 5)) * spanX,
    footprint.minY + ((gy - 2) / (gridSize - 5)) * spanY,
  ];
  const mark = (x: number, y: number, radius = 1) => {
    for (let oy = -radius; oy <= radius; oy += 1) {
      for (let ox = -radius; ox <= radius; ox += 1) {
        const nx = x + ox;
        const ny = y + oy;
        if (nx >= 0 && nx < gridSize && ny >= 0 && ny < gridSize) grid[ny * gridSize + nx] = 1;
      }
    }
  };

  walls.forEach((wall) => {
    const [x1, y1] = toGrid(...wall.start);
    const [x2, y2] = toGrid(...wall.end);
    const steps = Math.max(Math.abs(x2 - x1), Math.abs(y2 - y1), 1);
    for (let step = 0; step <= steps; step += 1) {
      mark(Math.round(x1 + ((x2 - x1) * step) / steps), Math.round(y1 + ((y2 - y1) * step) / steps));
    }
  });
  for (let coordinate = 2; coordinate < gridSize - 2; coordinate += 1) {
    mark(coordinate, 2);
    mark(coordinate, gridSize - 3);
    mark(2, coordinate);
    mark(gridSize - 3, coordinate);
  }

  const visited = new Uint8Array(grid.length);
  const rooms: DetectedRoom[] = [];
  for (let y = 3; y < gridSize - 3; y += 1) {
    for (let x = 3; x < gridSize - 3; x += 1) {
      const start = y * gridSize + x;
      if (grid[start] || visited[start]) continue;
      const queue: Array<[number, number]> = [[x, y]];
      visited[start] = 1;
      let cells = 0;
      let minX = x;
      let minY = y;
      let maxX = x;
      let maxY = y;
      for (let cursor = 0; cursor < queue.length; cursor += 1) {
        const [cx, cy] = queue[cursor];
        cells += 1;
        minX = Math.min(minX, cx);
        minY = Math.min(minY, cy);
        maxX = Math.max(maxX, cx);
        maxY = Math.max(maxY, cy);
        [[-1, 0], [1, 0], [0, -1], [0, 1]].forEach(([ox, oy]) => {
          const nx = cx + ox;
          const ny = cy + oy;
          if (nx < 3 || nx >= gridSize - 3 || ny < 3 || ny >= gridSize - 3) return;
          const next = ny * gridSize + nx;
          if (grid[next] || visited[next]) return;
          visited[next] = 1;
          queue.push([nx, ny]);
        });
      }
      if (cells < gridSize * gridSize * 0.012) continue;
      const [px1, py1] = toPixel(minX, minY);
      const [px2, py2] = toPixel(maxX + 1, maxY + 1);
      rooms.push({
        id: `room-${rooms.length + 1}`,
        polygon: [[px1, py1], [px2, py1], [px2, py2], [px1, py2]],
        areaPx: (px2 - px1) * (py2 - py1),
        confidence: clamp(0.55 + Math.min(0.3, cells / (gridSize * gridSize) * 3), 0.55, 0.85),
      });
      if (rooms.length >= 20) return rooms;
    }
  }
  return rooms;
}

export function inspectStructureEvidence(
  pixels: ArrayLike<number>,
  width: number,
  height: number,
  region: SourceRegion,
) {
  const bounds = paperBounds(pixels, width, height, region);
  const threshold = otsuThreshold(pixels, width, bounds);
  const windowMask = createMask(pixels, width, height, bounds, Math.min(205, threshold + 26));
  const mediumMask = createMask(pixels, width, height, bounds, Math.min(210, threshold + 40));
  const strongMask = createMask(pixels, width, height, bounds, threshold);
  const minimumDimension = Math.max(1, Math.min(bounds.maxX - bounds.minX, bounds.maxY - bounds.minY));
  // The oriented run must be longer than a normal wall is thick; otherwise
  // perpendicular walls join the mask into one building-sized component.
  const minimumRun = Math.max(12, Math.round(minimumDimension * 0.06));
  const horizontalComponents = maskComponents(
    markLongRuns(strongMask, width, height, bounds, "horizontal", minimumRun),
    width,
    height,
    bounds,
    "horizontal",
  );
  const verticalComponents = maskComponents(
    markLongRuns(strongMask, width, height, bounds, "vertical", minimumRun),
    width,
    height,
    bounds,
    "vertical",
  );
  const horizontal = splitByStrokeSupport(
    horizontalComponents,
    strongMask,
    width,
    height,
    minimumRun,
  );
  const vertical = splitByStrokeSupport(
    verticalComponents,
    strongMask,
    width,
    height,
    minimumRun,
  );
  const rawSegments = mergeOverlaps([...horizontal, ...vertical]).filter((segment) => (
    segment.to - segment.from >= minimumRun
    && segment.thickness <= minimumDimension * 0.095
  ));
  const wallThickness = clamp(weightedPercentile(rawSegments.filter((segment) => segment.to - segment.from >= minimumDimension * 0.1), 0.66), 2, minimumDimension * 0.07);
  // Outdoor rails and stair traces are legitimately thin and retain the raw
  // segments. Only wall candidates trim a lone thick run away from an attached
  // thin annotation such as a measurement line.
  const wallSegments = mergeOverlaps([
    ...splitByStrokeSupport(horizontalComponents, strongMask, width, height, minimumRun, true),
    ...splitByStrokeSupport(verticalComponents, strongMask, width, height, minimumRun, true),
  ]).filter((segment) => (
    segment.to - segment.from >= minimumRun
    && segment.thickness <= minimumDimension * 0.095
  ));
  const structural = structuralSegments(wallSegments, minimumDimension, wallThickness);

  // A second, fainter tier catches thin partitions that never reach the
  // strong-mask threshold at all. It is only a candidate pool here: the
  // anchoring rule that turns a candidate into a wall runs once the
  // footprint is known, in detectFloorStructureAligned.
  const mediumHorizontalComponents = maskComponents(
    markLongRuns(mediumMask, width, height, bounds, "horizontal", minimumRun),
    width,
    height,
    bounds,
    "horizontal",
  );
  const mediumVerticalComponents = maskComponents(
    markLongRuns(mediumMask, width, height, bounds, "vertical", minimumRun),
    width,
    height,
    bounds,
    "vertical",
  );
  const mediumCandidates = mergeOverlaps([
    ...splitByStrokeSupport(mediumHorizontalComponents, mediumMask, width, height, minimumRun),
    ...splitByStrokeSupport(mediumVerticalComponents, mediumMask, width, height, minimumRun),
  ]).filter((segment) => (
    segment.to - segment.from >= minimumRun
    && segment.thickness >= 1
    && segment.thickness <= Math.max(3, wallThickness * 0.62)
    && !isDimensionAnnotation(mediumMask, width, height, segment)
  ));
  const lightWallThickness = clamp(weightedPercentile(mediumCandidates, 0.6), 1, Math.max(1, wallThickness * 0.5));

  return { bounds, threshold, strongMask, mediumMask, windowMask, rawSegments, wallThickness, lightWallThickness, structural, mediumCandidates, minimumDimension, minimumRun };
}

/**
 * Trim a detected stair box back to the walls that actually enclose its shaft.
 *
 * `detectStairs` builds its box from pairs of long parallel "rail" strokes, so
 * regularly-spaced linework in an adjacent room — closet shelving is the common
 * case, its shelves look exactly like treads — can be paired with a real stair
 * rail and stretch the box across the dividing wall. The stair then reads as
 * wider than it is, which in turn puts the stairwell opening (and therefore the
 * railing around it) in the wrong place.
 *
 * A stair shaft is physically bounded by the walls around it, so any wall lying
 * strictly inside the box, parallel to the run and spanning it, is a boundary
 * the stair cannot cross. Clamp the box to the outermost such wall on each side.
 *
 * Deliberately conservative — a side is only pulled in when the wall spans
 * essentially the whole box, extends past it (proving it is a building wall
 * rather than a stringer belonging to the stair itself), and leaves a box still
 * large enough to be a stair. Fallback in every other case: the box is unchanged.
 */
/**
 * Separate a turned stair into its winder and its straight flight.
 *
 * A winder's treads fan out to fill the full width of the shaft, while the
 * flight below runs at a constant, narrower width down one side. Measuring how
 * far the linework reaches across the shaft at each point along the run
 * therefore gives a step profile: wide over the winder, narrow over the flight,
 * with a sharp edge between them. The bounding box of the two is the rectangle
 * that was being modelled before, which covers open floor in the corner the
 * stair turns away from.
 *
 * The scan reaches past the detected box out to the walls flanking the shaft,
 * because the box is fitted to the flight's parallel treads and clips the
 * winder where it sweeps wider. Walls bound it so that a neighbouring room's
 * contents cannot be read as part of the stair.
 */
export function splitStairIntoParts(
  stair: DetectedStair,
  mask: Uint8Array,
  width: number,
  height: number,
  walls: DetectedWall[],
): DetectedStair {
  const vertical = stair.runAxis === "vertical";
  const runFrom = Math.round(vertical ? stair.y : stair.x);
  const runTo = Math.round(vertical ? stair.y + stair.height : stair.x + stair.width);
  const crossFrom = vertical ? stair.x : stair.y;
  const crossTo = vertical ? stair.x + stair.width : stair.y + stair.height;
  if (runTo - runFrom < 8) return stair;

  // A winder reaches at most a shaft's width beyond the flight, and never past
  // the walls enclosing it.
  const crossCenter = (crossFrom + crossTo) / 2;
  const reach = (crossTo - crossFrom) * 1.2;
  let scanFrom = crossFrom - reach;
  let scanTo = crossTo + reach;
  for (const wall of walls) {
    if (wall.axis !== stair.runAxis) continue;
    const line = vertical ? (wall.start[0] + wall.end[0]) / 2 : (wall.start[1] + wall.end[1]) / 2;
    const half = wall.thickness / 2;
    const spanFrom = Math.min(vertical ? wall.start[1] : wall.start[0], vertical ? wall.end[1] : wall.end[0]);
    const spanTo = Math.max(vertical ? wall.start[1] : wall.start[0], vertical ? wall.end[1] : wall.end[0]);
    if (spanTo < runFrom || spanFrom > runTo) continue;
    // Clamp to the face nearest the shaft, so a wall the box already overlaps
    // still bounds the scan.
    if (line < crossCenter) scanFrom = Math.max(scanFrom, line + half);
    else scanTo = Math.min(scanTo, line - half);
  }
  scanFrom = Math.max(0, Math.round(scanFrom));
  scanTo = Math.min((vertical ? width : height) - 1, Math.round(scanTo));
  if (scanTo - scanFrom < 4) return stair;

  const inked = (along: number, across: number) => {
    const x = vertical ? across : along;
    const y = vertical ? along : across;
    if (x < 0 || x >= width || y < 0 || y >= height) return false;
    return mask[y * width + x] === 1;
  };

  // Follow each tread out from the shaft rather than taking the widest ink on
  // the line: a tread is one continuous stroke, so anything separated from it
  // by clear space belongs to something else in the room.
  const GAP = 2;
  const spans: Array<{ from: number; to: number } | null> = [];
  for (let along = runFrom; along <= runTo; along += 1) {
    let seedFrom: number | null = null;
    let seedTo = 0;
    for (let across = Math.round(crossFrom); across <= Math.round(crossTo); across += 1) {
      if (!inked(along, across)) continue;
      if (seedFrom === null) seedFrom = across;
      seedTo = across;
    }
    if (seedFrom === null) { spans.push(null); continue; }
    let from = seedFrom;
    for (let across = seedFrom - 1, gap = 0; across >= scanFrom; across -= 1) {
      if (inked(along, across)) { from = across; gap = 0; } else if ((gap += 1) > GAP) break;
    }
    let to = seedTo;
    for (let across = seedTo + 1, gap = 0; across <= scanTo; across += 1) {
      if (inked(along, across)) { to = across; gap = 0; } else if ((gap += 1) > GAP) break;
    }
    spans.push({ from, to });
  }
  const measured = spans.map((s) => (s ? s.to - s.from + 1 : 0));
  // Between treads a row catches only a stringer or two, which says nothing
  // about how far the stair reaches there. Count a row only where a tread
  // actually crosses it, so the profile reflects tread length rather than how
  // densely the flight happens to be drawn.
  const widest = Math.max(...measured);
  const extents = measured.map((e) => (e >= widest * 0.3 ? e : 0));
  if (extents.filter((e) => e > 0).length < 6) return stair;

  // The split that best separates a wide band from a narrow one.
  const mean = (from: number, to: number) => {
    let sum = 0; let count = 0;
    for (let i = from; i < to; i += 1) { if (extents[i] > 0) { sum += extents[i]; count += 1; } }
    return count ? sum / count : 0;
  };
  const minBand = Math.max(3, Math.round((runTo - runFrom) * 0.18));
  let best: { at: number; wide: number; narrow: number; ratio: number } | null = null;
  for (let i = minBand; i <= extents.length - minBand; i += 1) {
    const a = mean(0, i);
    const b = mean(i, extents.length);
    if (!a || !b) continue;
    const ratio = Math.max(a, b) / Math.min(a, b);
    if (!best || ratio > best.ratio) best = { at: i, wide: Math.max(a, b), narrow: Math.min(a, b), ratio };
  }
  // Below this the shaft is one straight flight and the box already describes it.
  if (!best || best.ratio < 1.3) return stair;

  const wideFirst = mean(0, best.at) > mean(best.at, extents.length);
  const range = (from: number, to: number) => {
    let lo = Number.POSITIVE_INFINITY; let hi = Number.NEGATIVE_INFINITY;
    for (let i = from; i < to; i += 1) {
      const s = spans[i];
      if (!s) continue;
      lo = Math.min(lo, s.from);
      hi = Math.max(hi, s.to);
    }
    return Number.isFinite(lo) ? { lo, hi } : null;
  };
  const wideRange = wideFirst ? range(0, best.at) : range(best.at, extents.length);
  const narrowRange = wideFirst ? range(best.at, extents.length) : range(0, best.at);
  if (!wideRange || !narrowRange) return stair;

  const splitAt = runFrom + best.at;
  const band = (lo: number, hi: number, from: number, to: number) => (vertical
    ? { x: lo, y: from, width: hi - lo, height: to - from }
    : { x: from, y: lo, width: to - from, height: hi - lo });
  const winder = wideFirst
    ? band(wideRange.lo, wideRange.hi, runFrom, splitAt)
    : band(wideRange.lo, wideRange.hi, splitAt, runTo);
  const flight = wideFirst
    ? band(narrowRange.lo, narrowRange.hi, splitAt, runTo)
    : band(narrowRange.lo, narrowRange.hi, runFrom, splitAt);

  // The box becomes the pair's bounds, so a winder clipped by the original fit
  // is recovered rather than lost.
  const minX = Math.min(winder.x, flight.x);
  const minY = Math.min(winder.y, flight.y);
  const maxX = Math.max(winder.x + winder.width, flight.x + flight.width);
  const maxY = Math.max(winder.y + winder.height, flight.y + flight.height);
  return { ...stair, x: minX, y: minY, width: maxX - minX, height: maxY - minY, winder, flight };
}

/**
 * Pull the ends of a stair run back to the walls that cross it.
 *
 * `clampStairsToFlankingWalls` bounds the shaft sideways, using the walls that
 * run alongside it. Nothing bounded the run itself, so a flight whose treads
 * were paired with linework beyond the building — a dimension tick above the
 * façade is enough — was left reaching through the exterior wall and modelled
 * outside the house.
 *
 * A wall crossing the run is a hard end: the flight stops at its face. Only the
 * ends are clamped, never a wall lying across the middle of the box, which
 * would halve a stair that legitimately passes a partition on its way up.
 */
export function clampStairRunToCrossingWalls(
  stairs: DetectedStair[],
  walls: DetectedWall[],
): DetectedStair[] {
  if (!stairs.length || !walls.length) return stairs;
  const MIN_SPAN_COVERAGE = 0.6; // the wall must cross most of the stair's width
  const MIN_REMAINING = 0.45;    // never shrink the run below this of the original

  return stairs.map((stair) => {
    const vertical = stair.runAxis === "vertical";
    const crossMin = vertical ? stair.x : stair.y;
    const crossMax = vertical ? stair.x + stair.width : stair.y + stair.height;
    const runMin = vertical ? stair.y : stair.x;
    const runMax = vertical ? stair.y + stair.height : stair.x + stair.width;
    const originalRun = runMax - runMin;

    let newMin = runMin;
    let newMax = runMax;

    for (const wall of walls) {
      if (wall.axis === stair.runAxis) continue;
      const line = vertical
        ? (wall.start[1] + wall.end[1]) / 2
        : (wall.start[0] + wall.end[0]) / 2;
      const half = wall.thickness / 2;
      const bandFrom = line - half;
      const bandTo = line + half;
      if (bandTo <= runMin || bandFrom >= runMax) continue;

      const wallCrossFrom = Math.min(
        vertical ? wall.start[0] : wall.start[1],
        vertical ? wall.end[0] : wall.end[1],
      );
      const wallCrossTo = Math.max(
        vertical ? wall.start[0] : wall.start[1],
        vertical ? wall.end[0] : wall.end[1],
      );
      const overlap = Math.max(0, Math.min(wallCrossTo, crossMax) - Math.max(wallCrossFrom, crossMin));
      if (overlap < (crossMax - crossMin) * MIN_SPAN_COVERAGE) continue;

      // Only a wall near an end is one the box has overshot into. A partition
      // further along is one the flight passes on its way up, and clamping to
      // it would cut the run in half.
      const endZone = (runMax - runMin) * 0.3;
      if (bandTo <= runMin + endZone) newMin = Math.max(newMin, bandTo);
      else if (bandFrom >= runMax - endZone) newMax = Math.min(newMax, bandFrom);
    }

    if (newMax - newMin >= originalRun - 0.5) return stair;
    if (newMax - newMin < originalRun * MIN_REMAINING) return stair;

    return vertical
      ? { ...stair, y: newMin, height: newMax - newMin }
      : { ...stair, x: newMin, width: newMax - newMin };
  });
}

function clampStairsToFlankingWalls(
  stairs: DetectedStair[],
  walls: DetectedWall[],
  wallThickness: number,
): DetectedStair[] {
  if (!stairs.length || !walls.length) return stairs;
  const MIN_SPAN_COVERAGE = 0.9;   // the wall must span ~all of the stair's run
  const MIN_OVERHANG = wallThickness * 0.5; // and continue past it on some side
  const MIN_REMAINING = 0.55;      // never shrink a side below this of the original

  return stairs.map((stair) => {
    const vertical = stair.runAxis === "vertical";
    // Cross axis = perpendicular to the run. Walls bounding the shaft run
    // parallel to the stair's run, so they share its axis.
    const crossMin = vertical ? stair.x : stair.y;
    const crossMax = vertical ? stair.x + stair.width : stair.y + stair.height;
    const runMin = vertical ? stair.y : stair.x;
    const runMax = vertical ? stair.y + stair.height : stair.x + stair.width;
    const crossCenter = (crossMin + crossMax) / 2;
    const originalCross = crossMax - crossMin;
    const inset = wallThickness * 0.75; // ignore walls sitting on the box edge

    let newMin = crossMin;
    let newMax = crossMax;

    for (const wall of walls) {
      if (wall.axis !== stair.runAxis) continue;
      const line = vertical ? (wall.start[0] + wall.end[0]) / 2 : (wall.start[1] + wall.end[1]) / 2;
      if (line <= crossMin + inset || line >= crossMax - inset) continue;

      const wallRunFrom = Math.min(vertical ? wall.start[1] : wall.start[0], vertical ? wall.end[1] : wall.end[0]);
      const wallRunTo = Math.max(vertical ? wall.start[1] : wall.start[0], vertical ? wall.end[1] : wall.end[0]);
      const overlap = Math.max(0, Math.min(wallRunTo, runMax) - Math.max(wallRunFrom, runMin));
      if (overlap < (runMax - runMin) * MIN_SPAN_COVERAGE) continue;
      // A stringer or tread edge stops with the stair; a real wall runs past it.
      if (wallRunFrom > runMin - MIN_OVERHANG && wallRunTo < runMax + MIN_OVERHANG) continue;

      if (line < crossCenter) newMin = Math.max(newMin, line);
      else newMax = Math.min(newMax, line);
    }

    if (newMax - newMin >= originalCross - 0.5) return stair;
    if (newMax - newMin < originalCross * MIN_REMAINING) return stair;

    return vertical
      ? { ...stair, x: newMin, width: newMax - newMin }
      : { ...stair, y: newMin, height: newMax - newMin };
  });
}

/** Ink thickness perpendicular to the wall's run at position `p` along the run.
 *  Samples three adjacent run rows and returns the median count for robustness. */
function localThicknessAt(
  mask: Uint8Array,
  width: number,
  height: number,
  axis: Axis,
  line: number,
  p: number,
  maxHalf: number,
): number {
  const counts: number[] = [];
  for (let d = -1; d <= 1; d += 1) {
    let c = 0;
    for (let off = -maxHalf; off <= maxHalf; off += 1) {
      const x = axis === "vertical" ? Math.round(line + off) : Math.round(p + d);
      const y = axis === "vertical" ? Math.round(p + d) : Math.round(line + off);
      if (x < 0 || x >= width || y < 0 || y >= height) continue;
      if (mask[y * width + x]) c += 1;
    }
    counts.push(c);
  }
  counts.sort((a, b) => a - b);
  return counts[1];
}

/**
 * For walls that run along a stairwell opening edge, analyse the ink thickness
 * profile and mark any consistently thin sub-spans as `railSpans` — these are
 * drawn as a balustrade (double-line) rather than a structural wall.
 *
 * Only fires when the profile is genuinely mixed (≥15 % thick AND ≥15 % thin),
 * so a uniformly thin partition (light wall) or uniformly thick wall is never
 * split. Fallback: the wall stays as-is and renders solid.
 */
function markBalustradeSpans(
  walls: DetectedWall[],
  stairs: DetectedStair[],
  mediumMask: Uint8Array,
  width: number,
  height: number,
  wallThickness: number,
): DetectedWall[] {
  if (!stairs.length) return walls;
  // Balustrades are drawn as thin double-lines (2–4 px). Structural walls are 6–12 px.
  // Threshold at ~55 % of the measured wallThickness catches the 4-px balustrade strokes
  // on fp-001 while excluding everything ≥ 5 px (structural or lighter partitions).
  const THIN_THRESHOLD = Math.max(4, wallThickness * 0.55);
  // The detected stair bounding box can extend up to 2–3 wall-thicknesses beyond the
  // balustrade because the measurement includes surrounding wall linework. Use a
  // looser tolerance so the balustrade wall is reliably found.
  const STAIR_TOLERANCE = wallThickness * 3;
  const SAMPLES = 60;
  const MIN_THIN_FRACTION = 0.15;
  const MIN_THICK_FRACTION = 0.15;
  // A balustrade run is a meaningful share of its wall AND long enough to be
  // more than a stroke artefact. Expressed as a fraction first: an absolute
  // pixel floor alone rejects short walls that exist only to edge the stairwell,
  // which is exactly where balustrades live.
  const MIN_SPAN_FRACTION = 0.2;
  const MIN_SPAN_PX = wallThickness * 2;

  return walls.map((wall) => {
    const wallCenterCoord = wall.axis === "vertical"
      ? (wall.start[0] + wall.end[0]) / 2
      : (wall.start[1] + wall.end[1]) / 2;

    const wallRunFrom = wall.axis === "vertical" ? Math.min(wall.start[1], wall.end[1]) : Math.min(wall.start[0], wall.end[0]);
    const wallRunTo = wall.axis === "vertical" ? Math.max(wall.start[1], wall.end[1]) : Math.max(wall.start[0], wall.end[0]);

    const onEdge = stairs.some((stair) => {
      const nearSide = wall.axis === "vertical"
        ? (Math.abs(wallCenterCoord - stair.x) <= STAIR_TOLERANCE || Math.abs(wallCenterCoord - (stair.x + stair.width)) <= STAIR_TOLERANCE)
        : (Math.abs(wallCenterCoord - stair.y) <= STAIR_TOLERANCE || Math.abs(wallCenterCoord - (stair.y + stair.height)) <= STAIR_TOLERANCE);
      if (!nearSide) return false;
      // Sharing the stair's line is not enough — a wall directly above or below
      // the shaft does that too. It must actually run alongside the stair.
      const stairFrom = wall.axis === "vertical" ? stair.y : stair.x;
      const stairTo = wall.axis === "vertical" ? stair.y + stair.height : stair.x + stair.width;
      const overlap = Math.min(wallRunTo, stairTo) - Math.max(wallRunFrom, stairFrom);
      return overlap >= (stairTo - stairFrom) * 0.5;
    });

    if (!onEdge) return wall;

    const runStart = wall.axis === "vertical" ? wall.start[1] : wall.start[0];
    const runEnd = wall.axis === "vertical" ? wall.end[1] : wall.end[0];
    const line = wall.axis === "vertical"
      ? (wall.start[0] + wall.end[0]) / 2
      : (wall.start[1] + wall.end[1]) / 2;
    const maxHalf = Math.max(4, Math.round(wallThickness * 1.5));

    const thicknesses: number[] = [];
    for (let i = 0; i < SAMPLES; i += 1) {
      const p = runStart + (runEnd - runStart) * i / (SAMPLES - 1);
      thicknesses.push(localThicknessAt(mediumMask, width, height, wall.axis, line, p, maxHalf));
    }

    const thinCount = thicknesses.filter((t) => t > 0 && t <= THIN_THRESHOLD).length;
    const thickCount = thicknesses.filter((t) => t > THIN_THRESHOLD).length;
    if (thinCount < SAMPLES * MIN_THIN_FRACTION || thickCount < SAMPLES * MIN_THICK_FRACTION) return wall;

    const runLength = Math.abs(runEnd - runStart);
    const longEnough = (from: number, to: number) => (
      to - from >= MIN_SPAN_FRACTION && (to - from) * runLength >= MIN_SPAN_PX
    );
    const railSpans: Array<[number, number]> = [];
    let spanStart: number | null = null;
    for (let i = 0; i < SAMPLES; i += 1) {
      const thin = thicknesses[i] > 0 && thicknesses[i] <= THIN_THRESHOLD;
      const frac = i / (SAMPLES - 1);
      if (thin) {
        if (spanStart === null) spanStart = frac;
      } else if (spanStart !== null) {
        if (longEnough(spanStart, frac)) railSpans.push([spanStart, frac]);
        spanStart = null;
      }
    }
    if (spanStart !== null && longEnough(spanStart, 1)) railSpans.push([spanStart, 1]);

    // Where a balustrade meets a wall at a corner, the crossing linework thickens
    // the last samples and the run stops just short of the end. Left alone that
    // renders as a sliver of full-height wall standing in the corner, so a
    // remainder shorter than a couple of wall thicknesses is closed up.
    const snap = wallThickness * 2;
    const snapped = railSpans.map(([from, to]): [number, number] => [
      from * runLength <= snap ? 0 : from,
      (1 - to) * runLength <= snap ? 1 : to,
    ]);

    return snapped.length ? { ...wall, railSpans: snapped } : wall;
  });
}

function detectFloorStructureAligned(
  pixels: ArrayLike<number>,
  width: number,
  height: number,
  region: SourceRegion,
): DetectedStructure {
  const { bounds, threshold, strongMask, mediumMask, windowMask, rawSegments, wallThickness, lightWallThickness, structural, mediumCandidates, minimumDimension, minimumRun } = inspectStructureEvidence(pixels, width, height, region);
  const thickCore = structural.filter((segment) => segment.thickness >= wallThickness * 0.62 || segment.to - segment.from >= minimumDimension * 0.3);
  const tracedBounds = segmentBounds(thickCore.length ? thickCore : structural, bounds);
  // Prefer the traced walls, which sit on the wall centre lines, but fall back
  // to the ink envelope wherever tracing clearly under-covers the drawing.
  // Tracing collapses on plans drawn in colour or in very thin strokes, and a
  // footprint that is far smaller than the ink is always the tracer failing,
  // never a real building — nothing structural can lie outside the ink.
  const footprintBounds = reconcileFootprint(tracedBounds, inkEnvelope(pixels, width, bounds, threshold));
  const anchorTolerance = Math.max(3, wallThickness * 0.75);
  const lightStructural = lightStructuralSegments(mediumCandidates, structural, minimumDimension, footprintBounds, anchorTolerance);
  const anchoredHeavy = recoverWallAnchors(structural, strongMask, width, height, footprintBounds, wallThickness, "heavy");
  const anchoredLight = lightStructural.length
    ? recoverWallAnchors(lightStructural, mediumMask, width, height, footprintBounds, lightWallThickness, "light")
    : [];
  const anchoredStructural = [...anchoredHeavy, ...anchoredLight];
  const outdoorAreas = detectOutdoorAreas(rawSegments, footprintBounds, wallThickness);
  const walls = recoverEmbeddedOpenings(
    trimUnsupportedInteriorWallTails(
      buildWalls(anchoredStructural, mediumMask, windowMask, width, height, wallThickness, footprintBounds),
      strongMask,
      width,
      height,
      minimumRun,
      footprintBounds,
    ),
    strongMask,
    mediumMask,
    windowMask,
    width,
    height,
    footprintBounds,
  );
  // Trim stair boxes that swallowed adjacent-room linework before anything
  // downstream (stairwell opening, railings, obstacles) is derived from them.
  const stairs = clampStairRunToCrossingWalls(
    clampStairsToFlankingWalls(
      detectStairs(rawSegments, mediumMask, width, height, footprintBounds, minimumDimension, wallThickness),
      walls,
      wallThickness,
    ),
    walls,
  ).map((stair) => splitStairIntoParts(stair, mediumMask, width, height, walls));
  const walledWithRails = markBalustradeSpans(walls, stairs, mediumMask, width, height, wallThickness);
  const openingCount = walledWithRails.reduce((sum, wall) => sum + wall.openings.length, 0);
  const topologyVotes = walledWithRails.filter((wall) => walledWithRails.some((other) => other !== wall && (
    Math.hypot(wall.start[0] - other.start[0], wall.start[1] - other.start[1]) <= wallThickness * 2.5
    || Math.hypot(wall.end[0] - other.end[0], wall.end[1] - other.end[1]) <= wallThickness * 2.5
    || (wall.axis !== other.axis && wall.start[0] <= other.end[0] + wallThickness && wall.end[0] >= other.start[0] - wallThickness)
  ))).length;
  const confidence = clamp(
    0.46
      + Math.min(0.24, walledWithRails.length * 0.018)
      + Math.min(0.1, topologyVotes * 0.012)
      + Math.min(0.08, openingCount * 0.012)
      + (outdoorAreas.length ? 0.03 : 0)
      + (stairs.length ? 0.025 : 0),
    walledWithRails.length >= 4 ? 0.58 : 0.38,
    0.94,
  );

  const rooms = detectRooms(walledWithRails, footprintBounds);
  // Furniture detection: use the medium mask (thin strokes visible) and keep
  // results only within the footprint interior. Stair shafts (their treads
  // read as a grid of boxes) and wall corridors (their corners read as
  // bordered boxes) are the dominant false-positive sources, so both are
  // supplied as obstacles. Conservative — nothing is emitted when ambiguous.
  // Pad by less than half a wall so the margin stays inside the wall band. The
  // box has already been clamped to the walls flanking the shaft, so a wider
  // margin reaches into the neighbouring room and suppresses the joinery drawn
  // against the far side of that wall — a walk-in's shelving, typically.
  const stairMargin = wallThickness * 0.4;
  const stairObstacles: FixtureObstacle[] = stairs.map((stair) => ({
    minX: stair.x - stairMargin,
    minY: stair.y - stairMargin,
    maxX: stair.x + stair.width + stairMargin,
    maxY: stair.y + stair.height + stairMargin,
  }));
  // Walls are not supplied as obstacles: the component detector erases them
  // from its own working mask, and wall-adjacent fixtures — which is most of
  // them — would otherwise be discarded for overlapping a wall band.
  const detectorWalls: DetectorWall[] = walledWithRails.map((w) => ({
    axis: w.axis,
    start: w.start,
    end: w.end,
    thickness: w.thickness,
  }));
  // The component fixture detector classifies by real-world size, so it needs a
  // pixel-to-metre ratio here — before the shared project scale is resolved
  // across levels. Derive a provisional one from this level's own symbol-
  // confirmed doors, using the same reference width as resolveScaleFromDoors.
  const localDoorWidths = walledWithRails
    .flatMap((wall) => wall.openings)
    .filter((opening) => opening.kind === "door" && opening.evidence === "symbol")
    .map((opening) => opening.width)
    .filter((openingWidth) => openingWidth >= 12 && openingWidth <= 90)
    .sort((a, b) => a - b);
  const localMetresPerPixel = localDoorWidths.length
    ? REFERENCE_DOOR_WIDTH_METRES / localDoorWidths[Math.floor(localDoorWidths.length / 2)]
    : undefined;
  const fixtures = detectFurniture(mediumMask, width, footprintBounds, wallThickness,
    stairObstacles, 60, detectorWalls, localMetresPerPixel);
  return {
    fixtureInputs: {
      mask: mediumMask,
      width,
      footprint: footprintBounds,
      wallThickness,
      obstacles: stairObstacles,
      walls: detectorWalls,
    },
    regionId: region.id,
    sourceWidth: width,
    sourceHeight: height,
    walls: walledWithRails,
    outdoorAreas,
    stairs,
    rooms,
    fixtures,
    footprint: {
      x: footprintBounds.minX,
      y: footprintBounds.minY,
      width: Math.max(1, footprintBounds.maxX - footprintBounds.minX),
      height: Math.max(1, footprintBounds.maxY - footprintBounds.minY),
    },
    roomCount: rooms.length,
    confidence,
    diagnostics: {
      threshold,
      wallThickness,
      geometryVotes: walledWithRails.length,
      topologyVotes,
      openingVotes: openingCount,
      stairVotes: stairs.length,
    },
  };
}

export function detectFloorStructure(
  pixels: ArrayLike<number>,
  width: number,
  height: number,
  region: SourceRegion,
): DetectedStructure {
  const sourceBounds = paperBounds(pixels, width, height, region);
  const sourceThreshold = otsuThreshold(pixels, width, sourceBounds);
  const sourceMask = createMask(pixels, width, height, sourceBounds, sourceThreshold);
  const rotationDegrees = estimateDominantPlanRotation(sourceMask, width, height, sourceBounds);
  const center: [number, number] = [
    (sourceBounds.minX + sourceBounds.maxX) / 2,
    (sourceBounds.minY + sourceBounds.maxY) / 2,
  ];
  const analysisPixels = rotationDegrees
    ? rotatePixelsAround(pixels, width, height, center, rotationDegrees)
    : pixels;
  const structure = detectFloorStructureAligned(analysisPixels, width, height, region);
  return {
    ...structure,
    sourceRotationDegrees: rotationDegrees,
    rotationCenter: center,
    diagnostics: {
      ...structure.diagnostics,
      rotationDegrees,
    },
  };
}

export function detectFloorStructures(
  pixels: ArrayLike<number>,
  width: number,
  height: number,
  regions: SourceRegion[],
) {
  const structures: Record<string, DetectedStructure> = Object.fromEntries(
    regions.map((region) => [region.id, detectFloorStructure(pixels, width, height, region)]),
  );

  // Fixtures are classified by real-world size, so they must all be measured
  // against one scale. Each level's own doors give an estimate good to about a
  // quarter, which is the difference between a kitchen run and a wardrobe, or
  // between an island and a bath — so once every level has been read, resolve
  // the scale they share and measure the fixtures again against it.
  const shared = resolveScaleFromDoors(structures);
  if (shared) {
    for (const structure of Object.values(structures)) {
      const inputs = structure.fixtureInputs;
      if (!inputs) continue;
      structure.fixtures = detectFurniture(
        inputs.mask,
        inputs.width,
        inputs.footprint,
        inputs.wallThickness,
        inputs.obstacles,
        60,
        inputs.walls,
        shared.metresPerPixel,
      );
    }
  }
  for (const structure of Object.values(structures)) delete structure.fixtureInputs;
  return structures;
}

function normalizedStairBox(stair: DetectedStair, footprint: DetectedStructure["footprint"]) {
  return {
    x: (stair.x - footprint.x) / Math.max(1, footprint.width),
    y: (stair.y - footprint.y) / Math.max(1, footprint.height),
    width: stair.width / Math.max(1, footprint.width),
    height: stair.height / Math.max(1, footprint.height),
    centerX: (stair.x + stair.width / 2 - footprint.x) / Math.max(1, footprint.width),
    centerY: (stair.y + stair.height / 2 - footprint.y) / Math.max(1, footprint.height),
  };
}

/**
 * Adjacent floorplans describe the same physical stair shaft with different
 * amounts of linework. Once the floor order is known, use the upper-floor
 * shaft (where the full opening is visible) as the shared normalized box for
 * the floor below. This keeps the analyser overlay and the 3D coordinate
 * conversion on one common vertical axis.
 */
export function alignAdjacentStairStructures(
  regions: SourceRegion[],
  structures: Record<string, DetectedStructure>,
) {
  const aligned = { ...structures };
  for (let index = regions.length - 2; index >= 0; index -= 1) {
    const lower = aligned[regions[index].id];
    const upper = aligned[regions[index + 1].id];
    if (!lower?.stairs.length || !upper?.stairs.length) continue;
    const upperStair = [...upper.stairs].sort((a, b) => b.confidence - a.confidence)[0];
    const upperBox = normalizedStairBox(upperStair, upper.footprint);
    const candidates = lower.stairs
      .map((stair) => {
        const box = normalizedStairBox(stair, lower.footprint);
        return {
          stair,
          box,
          distance: Math.hypot(box.centerX - upperBox.centerX, box.centerY - upperBox.centerY),
        };
      })
      .filter(({ stair }) => stair.runAxis === upperStair.runAxis)
      .sort((a, b) => a.distance - b.distance);
    const match = candidates[0];
    if (!match || match.distance > 0.22) continue;
    // Only correct drift between two readings of the same shaft. A winder
    // sweeps wider than the straight flight below it, so on a turned stair the
    // two plans genuinely show different widths — the upper floor its fan, the
    // lower its run — and forcing one onto the other squares off the turn and
    // reports both floors with the same box. When the widths disagree by more
    // than drift can explain, each floor keeps what its own plan shows.
    const upperCross = upperStair.runAxis === "vertical" ? upperBox.width : upperBox.height;
    const lowerCross = upperStair.runAxis === "vertical" ? match.box.width : match.box.height;
    const widthRatio = Math.max(upperCross, lowerCross) / Math.max(1e-6, Math.min(upperCross, lowerCross));
    if (widthRatio > 1.3) continue;
    // Align only the cross-axis (the shaft position visible in the slab opening)
    // from the upper floor. Preserve the lower floor's own run-axis extent so
    // that a ground-floor flight that spans more treads than the upper flight
    // retains its longer detected length.
    const projected: DetectedStair = upperStair.runAxis === "vertical"
      ? {
          ...match.stair,
          runAxis: "vertical",
          // cross-axis: x and width come from upper floor shaft
          x: lower.footprint.x + upperBox.x * lower.footprint.width,
          width: upperBox.width * lower.footprint.width,
          // run-axis: y and height preserved from lower floor's own detection
          confidence: Math.max(match.stair.confidence, upperStair.confidence * 0.94),
        }
      : {
          ...match.stair,
          runAxis: "horizontal",
          // cross-axis: y and height come from upper floor shaft
          y: lower.footprint.y + upperBox.y * lower.footprint.height,
          height: upperBox.height * lower.footprint.height,
          // run-axis: x and width preserved from lower floor's own detection
          confidence: Math.max(match.stair.confidence, upperStair.confidence * 0.94),
        };
    aligned[regions[index].id] = {
      ...lower,
      stairs: lower.stairs.map((stair) => stair.id === match.stair.id ? projected : stair),
    };
  }
  return aligned;
}

/** Representative clear width of a Danish residential interior door, used to
 * calibrate real-world scale from detected door openings when no dimension
 * text has been read. This is a provisional estimate, not a measurement: it
 * is only used when no user measurement or read dimension is available. */
const REFERENCE_DOOR_WIDTH_METRES = 0.89;

export type ProjectScale = {
  metresPerPixel: number;
  source: "door-width" | "user" | "provisional";
  confidence: number;
};

/**
 * Estimate one shared pixel-to-metre ratio for the whole project from the
 * median width of symbol-confirmed door openings, calibrated against a
 * representative Danish interior door. This is deliberately a single
 * estimate shared by every level: two floors of the same building must use
 * the same scale, which the previous per-level "10 metres wide" heuristic
 * did not guarantee.
 */
export function resolveScaleFromDoors(structures: Record<string, DetectedStructure>): ProjectScale | null {
  const doorWidths = Object.values(structures)
    .flatMap((structure) => structure.walls.flatMap((wall) => wall.openings))
    .filter((opening) => opening.kind === "door" && opening.evidence === "symbol")
    .map((opening) => opening.width)
    .filter((width) => width >= 12 && width <= 90)
    .sort((a, b) => a - b);
  if (doorWidths.length < 2) return null;
  const medianWidth = doorWidths[Math.floor(doorWidths.length / 2)];
  const metresPerPixel = REFERENCE_DOOR_WIDTH_METRES / medianWidth;

  const plausible = Object.values(structures).every((structure) => {
    const widthMetres = structure.footprint.width * metresPerPixel;
    const heightMetres = structure.footprint.height * metresPerPixel;
    return widthMetres >= 2.5 && widthMetres <= 40 && heightMetres >= 2.5 && heightMetres <= 40;
  });
  if (!plausible) return null;

  return {
    metresPerPixel,
    source: "door-width",
    confidence: clamp(0.4 + Math.min(0.3, doorWidths.length * 0.05), 0.4, 0.7),
  };
}

export function structureToLevel(
  structure: DetectedStructure,
  region: SourceRegion,
  index: number,
  sharedScale?: ProjectScale,
): Level {
  const footprint = structure.footprint;
  const pixelsToMetres = sharedScale ? sharedScale.metresPerPixel : 10 / Math.max(1, footprint.width);
  const sceneWidth = sharedScale ? footprint.width * pixelsToMetres : 10;
  const sceneDepth = sharedScale ? footprint.height * pixelsToMetres : clamp(footprint.height * pixelsToMetres, 4.2, 15);
  const centerX = footprint.x + footprint.width / 2;
  const centerY = footprint.y + footprint.height / 2;
  const toScene = ([x, y]: [number, number]): [number, number] => [
    (x - centerX) * pixelsToMetres,
    (y - centerY) * pixelsToMetres,
  ];

  const walls: Wall[] = structure.walls.map((wall) => {
    const clippedStart: [number, number] = [
      clamp(wall.start[0], footprint.x, footprint.x + footprint.width),
      clamp(wall.start[1], footprint.y, footprint.y + footprint.height),
    ];
    const clippedEnd: [number, number] = [
      clamp(wall.end[0], footprint.x, footprint.x + footprint.width),
      clamp(wall.end[1], footprint.y, footprint.y + footprint.height),
    ];
    const trim = Math.hypot(clippedStart[0] - wall.start[0], clippedStart[1] - wall.start[1]);
    const clippedLength = Math.hypot(clippedEnd[0] - clippedStart[0], clippedEnd[1] - clippedStart[1]);
    const start = toScene(clippedStart);
    const end = toScene(clippedEnd);
    const openings: Opening[] = wall.openings.flatMap((opening) => {
      const from = clamp(opening.offset - trim, 0, clippedLength);
      const to = clamp(opening.offset + opening.width - trim, 0, clippedLength);
      if (to - from < 2) return [];
      return [{
        kind: opening.kind,
        offset: from * pixelsToMetres,
        width: (to - from) * pixelsToMetres,
        height: opening.kind === "door" ? 2.12 : 1.3,
        sill: opening.kind === "window" ? 0.9 : undefined,
        confidence: opening.confidence,
      }];
    });
    // railSpans are fractions [0,1] of the original pixel wall run. Convert to
    // local scene metre offsets (same frame as opening.offset) by multiplying
    // by the original pixel length, subtracting the start trim, and scaling.
    const originalLength = Math.hypot(wall.end[0] - wall.start[0], wall.end[1] - wall.start[1]);
    const railSpans: Array<[number, number]> = (wall.railSpans ?? []).map(([from, to]): [number, number] => {
      const localFrom = Math.max(0, (from * originalLength - trim) * pixelsToMetres);
      const localTo = Math.min(clippedLength * pixelsToMetres, (to * originalLength - trim) * pixelsToMetres);
      return [localFrom, localTo];
    }).filter(([f, t]) => t - f > 0.05);

    return {
      id: `${region.id}-${wall.id}`,
      start,
      end,
      thickness: clamp(wall.thickness * pixelsToMetres, wall.weight === "light" ? 0.05 : 0.1, 0.42),
      openings,
      confidence: wall.confidence,
      weight: wall.weight,
      ...(railSpans.length ? { railSpans } : {}),
    };
  }).filter((wall) => Math.hypot(wall.end[0] - wall.start[0], wall.end[1] - wall.start[1]) > 0.05);

  const detectedOutdoor = region.hasOutdoorArea === false ? [] : structure.outdoorAreas;
  const outdoorSource = detectedOutdoor.length || !region.hasOutdoorArea
    ? detectedOutdoor
    : [{
      id: `manual-outdoor-${region.id}`,
      side: "bottom" as const,
      x: footprint.x + footprint.width * 0.08,
      y: footprint.y + footprint.height,
      width: footprint.width * 0.84,
      height: footprint.height * 0.24,
      confidence: 0.55,
    }];
  const outdoorAreas: OutdoorArea[] = outdoorSource.map((area) => {
    const areaWidth = area.width * pixelsToMetres;
    const areaDepth = area.height * pixelsToMetres;
    let x = (area.x + area.width / 2 - centerX) * pixelsToMetres;
    let z = (area.y + area.height / 2 - centerY) * pixelsToMetres;
    // Attach the detected platform to the corresponding slab edge. Pixel
    // evidence determines its span and depth, while this constraint prevents
    // small crop/footprint disagreements from moving it under or away from the
    // building in 3D.
    if (area.side === "bottom") z = sceneDepth / 2 + areaDepth / 2;
    if (area.side === "top") z = -sceneDepth / 2 - areaDepth / 2;
    if (area.side === "right") x = sceneWidth / 2 + areaWidth / 2;
    if (area.side === "left") x = -sceneWidth / 2 - areaWidth / 2;
    if (area.side === "top" || area.side === "bottom") {
      x = clamp(x, -sceneWidth / 2 + areaWidth / 2, sceneWidth / 2 - areaWidth / 2);
    } else {
      z = clamp(z, -sceneDepth / 2 + areaDepth / 2, sceneDepth / 2 - areaDepth / 2);
    }
    return {
      id: area.id,
      x,
      z,
      width: areaWidth,
      depth: areaDepth,
      side: area.side,
      confidence: area.confidence,
    };
  });
  const stairs: Stair[] = structure.stairs.map((stair) => {
    const rawWidth = stair.width * pixelsToMetres;
    const rawDepth = stair.height * pixelsToMetres;
    const crossAxis = stair.runAxis === "vertical" ? rawWidth : rawDepth;
    const runAxis = stair.runAxis === "vertical" ? rawDepth : rawWidth;
    const clampedCross = clamp(crossAxis, 0.8, 2.6);
    const clampedRun = clamp(runAxis, 1.2, 5);
    const toBox = (part: { x: number; y: number; width: number; height: number }) => ({
      x: (part.x + part.width / 2 - centerX) * pixelsToMetres,
      z: (part.y + part.height / 2 - centerY) * pixelsToMetres,
      width: part.width * pixelsToMetres,
      depth: part.height * pixelsToMetres,
    });
    // The parts describe the box that was measured. If that box had to be
    // clamped to a plausible size the two no longer agree, so drop them rather
    // than draw an L that does not fit its own bounds.
    const clamped = Math.abs(clampedCross - crossAxis) > 0.01 || Math.abs(clampedRun - runAxis) > 0.01;
    return {
      id: stair.id,
      x: (stair.x + stair.width / 2 - centerX) * pixelsToMetres,
      z: (stair.y + stair.height / 2 - centerY) * pixelsToMetres,
      width: stair.runAxis === "vertical" ? clampedCross : clampedRun,
      depth: stair.runAxis === "vertical" ? clampedRun : clampedCross,
      runAxis: stair.runAxis,
      stepCount: stair.stepCount,
      confidence: stair.confidence,
      ...(stair.ascend ? { ascend: stair.ascend } : {}),
      ...(stair.winder && stair.flight && !clamped
        ? { winder: toBox(stair.winder), flight: toBox(stair.flight) }
        : {}),
    };
  });
  const rooms: Room[] = structure.rooms.map((room) => ({
    id: room.id,
    polygon: room.polygon.map(toScene),
    area: Number((room.areaPx * pixelsToMetres * pixelsToMetres).toFixed(1)),
    confidence: room.confidence,
  }));

  const fixtures: Fixture[] = (structure.fixtures ?? []).map((f) => ({
    id: f.id,
    kind: f.kind,
    x: (f.x - centerX) * pixelsToMetres,
    z: (f.y - centerY) * pixelsToMetres,
    width: f.width * pixelsToMetres,
    depth: f.height * pixelsToMetres,
    rotation: f.rotation,
    confidence: f.confidence,
    ...(f.front ? { front: f.front } : {}),
  }));

  return {
    id: region.id,
    name: region.name,
    shortName: index === 0 ? "BASE" : `${index}F`,
    elevation: index * 3.05,
    ceilingHeight: index === 0 ? 2.7 : 2.55,
    area: Number((sceneWidth * sceneDepth).toFixed(1)),
    roomCount: structure.roomCount,
    wallCount: walls.length,
    openingCount: walls.reduce((sum, wall) => sum + (wall.openings?.length ?? 0), 0),
    scaleStatus: sharedScale ? "resolved" : "needed",
    slab: { width: sceneWidth, depth: sceneDepth, x: 0, z: 0 },
    walls,
    outdoorAreas,
    stairs,
    rooms,
    fixtures,
    floorTextureUrl: structure.floorTextureUrl,
    detectionConfidence: structure.confidence,
    source: "detected",
  };
}
